// Bunker Hill advisory-loop seed — Domain 5 (Case chain), Alarm, FailureMode,
// the Module 3 rule rows, and the KPI comparison-basis triad. Frozen at
// 08 Jun 2026 11:30, day shift, A Crew, startup month 4.
//
// Provenance convention (reconciliation R9 / seed-data rule R1): every literal
// cites the deck page (B: <page>), the module doc section (A §Mn), or the
// tim-scope arbitration id (S<n>); anything with no source is marked [syn].
// Nothing consumes these exports yet — consumers migrate under reconcile.T13.
// Nothing in bunker-hill-seed.ts duplicates the Case chain — see bunker-hill-advisory-seed.ts.

import {
  approvalGateSeatLabel,
} from '@/versions/v1/shared/bunker-hill-org-seed';
import { proposedZnRecBandForAgZone } from '@/versions/v1/shared/control-plan-band.derive';
import { resolveHandler } from '@/versions/v1/shared/org';
import type { IssueThread } from '@/versions/v1/schema/advisory-issue';
import type {
  Alarm,
  Case,
  CaseOutcome,
  FMEAUpdateReview,
  FailureMode,
  RecommendedAction,
  VariationEvent,
} from '@/versions/v1/schema/case';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import { causeRow } from '@/versions/v1/shared/bunker-hill-met-diagnostics-rules';
import type { KpiDefinition, KpiValue, Target } from '@/versions/v1/schema/target';
import { PB_RECOVERY_TABLE } from '@/versions/v1/shared/bunker-hill-seed';
import { UPTAKE_HISTORY_ACTIONS } from '@/versions/v1/shared/bunker-hill-uptake-history-seed';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { BM01_BEARING_TRIGGER } from '@/versions/v1/shared/bunker-hill-bearing-trend';
import { BM01_BEARING_PROPOSED_STOP } from '@/versions/v1/shared/bunker-hill-bearing-stop';

const BM01_BEARING_READING = READINGS.find((row) => row.id === 'rd-bm-brg-temp')!;

/** K20 round 1 (#717): the AG-CONC case's grade arrow is derived from the
 *  reading's own level against its design point — 12.14 vs 12.13 is *above*
 *  design, so the delta points up even though the case's excursion is the
 *  six-shift fall. A typed `direction` beside a derived comparison is what
 *  let the arrow contradict the level. */
const AG_GRADE_READING = READINGS.find((row) => row.id === 'rd-kpi-ag-grade')!;
const AG_GRADE_DIRECTION: 'up' | 'down' | 'flat' =
  (AG_GRADE_READING.value ?? 0) > (AG_GRADE_READING.design ?? 0)
    ? 'up'
    : (AG_GRADE_READING.value ?? 0) < (AG_GRADE_READING.design ?? 0)
      ? 'down'
      : 'flat';

export type { IssueThread };

/** [syn] — candidate inputs: open-case severity mix, alarm ack rate, sample adherence rollup (S15: no formula in either source). */
export const PLANT_HEALTH_PCT = 74;

/** B: Field Operator contacts; Paul §M3 §4 single accountable approval gate. */
export const APPROVAL_GATE_SEAT = approvalGateSeatLabel();

const MET_HANDLER = resolveHandler('metallurgist', '07:05')!;
/** The person holding `seat-senior-met` (L. Chen): a review at the Senior
 *  metallurgist seat names its holder, never the Metallurgist (P27 r1). */
const SENIOR_MET_REVIEWER = resolveHandler('senior-metallurgist', '2026-06-08T00:00')!.name;

/** Slide 05 footer, verbatim: "Generated: 08 Jun 2026 11:32 · Next auto-refresh: 12:00 · Model confidence: 80% avg". */
export const AI_PANEL_META = {
  generatedAt: '08 Jun 11:30', // B: slide 05 says 11:32 — moved to 11:30 so the last ranking is not after the 08 Jun 11:30 cursor (K19 review)
  nextRefreshAt: '12:00', // B: slide 05
  modelConfidenceAvgPct: 80, // B: slide 05
} as const;

/**
 * Confidence-meter colour bands. [syn] thresholds — slide 05 draws 91% green and
 * 82 / 78 / 71 % orange without stating the cut-offs (corpus map V8, agenda item).
 * Ordered high → low; the first band whose `min` the value meets applies.
 */
export const CONFIDENCE_BANDS: readonly { min: number; tone: 'ok' | 'warn' | 'bad' }[] = [
  { min: 90, tone: 'ok' },
  { min: 70, tone: 'warn' },
  { min: 0, tone: 'bad' },
];

// ---------------------------------------------------------------------------
// Alarms — four plant-wide (S12: the CRO view suppresses LOW; the GM and Ops
// Supervisor pages carry all four). Codes from the Ops Supervisor set (S12).
// Priority per the plant's P1–P4 philosophy (A §M1 §7); HIGH/MED/LOW derived.
// ---------------------------------------------------------------------------

export const ALARMS: readonly Alarm[] = [
  {
    id: 'alm-zn-ph',
    code: 'ZN-PH-01',
    priority: 'P2',
    message: 'Zn Float pH LOW 10.2 (SP=10.8)', // B: slide 12 verbatim (CRO's own screen; V10); S7 — 10.2 live, 10.8 the SP label; the 11.0–11.2 band belongs on the case card
    tag: 'AIC5002',
    raisedAt: '06:42', // B: Ops Supervisor alarm list
    state: 'acked',
    spawnedCaseId: 'case-zn-ph-response',
    origin: 'dcs', // the DCS's own ZN-PH-01 loop alarm — see review priority-likelihood M7
  },
  {
    id: 'alm-bm-brg',
    code: 'BM-BRG',
    priority: 'P3',
    message: 'Ball mill bearing temperature alarm', // B: alarm text is projected from the recorded raise-time sample and Reading thresholds.
    tag: 'TE-622-625',
    raisedAt: '03:15',
    state: 'active',
    origin: 'dcs', // a plant bearing-temperature alarm, not a product hypothesis
  },
  {
    id: 'alm-pump-04',
    code: 'PUMP-04',
    priority: 'P4',
    message: 'Tails pump 4 seal water flow low', // B: GM Key Alerts / Ops Supervisor
    raisedAt: '01:08',
    state: 'active',
    origin: 'dcs', // a plant seal-water alarm, not a product hypothesis
  },
  {
    id: 'alm-ag-conc',
    code: 'AG-CONC',
    priority: 'P3',
    // B: Ops Supervisor lists this in the alarm panel; per axis 7 it is a Case
    // (a diagnostic hypothesis with an action), so the alarm row only points at it.
    message: 'Ag grade in Pb conc six shifts falling — check 3418A (advisory, see case)',
    raisedAt: '04:10',
    state: 'acked',
    spawnedCaseId: 'case-ag-grade',
    // Not a DCS alarm: this row is the product's own diagnostic hypothesis
    // wearing an alarm's shape (its own message says "advisory, see case") —
    // review priority-likelihood M7. It must never shield a variable from
    // the CRO's excursion queue.
    origin: 'advisory',
  },
];

// ---------------------------------------------------------------------------
// Variation events — the four deck findings as detected departures (B: AI
// Performance), each matched to the rule behind it (confidence = deck value).
// ---------------------------------------------------------------------------

export const VARIATION_EVENTS: readonly VariationEvent[] = [
  {
    id: 've-zn-rec',
    subprocessId: 'sp-zn',
    variableId: 'mv-zn-ph',
    detectedAt: '2026-06-08T06:50', // [syn] — the morning case open; alarm at 06:42 (B)
    description: 'Zn recovery below design', // magnitude/description derived at render (journey-fidelity C38)
    magnitude: '82.3% vs design 85.1%', // derived at render — no ramp comparator on glass
    matchedRules: [{ ruleId: 'dr-zn-ph', confidencePct: 91 }], // B: AI Performance
  },
  {
    id: 've-ag-grade',
    subprocessId: 'sp-pb',
    variableId: 'mv-ag-grade',
    streamId: 'st-pbcon',
    detectedAt: '04:10',
    description: 'Ag grade in Pb/Ag concentrate six shifts falling, at design', // B: AI Performance finding 2; K20 (#717) — the level is the balance-implied 12.14 (at the 12.13 design), the excursion is the fall
    magnitude: '12.14 oz/t vs design 12.13 (+0.1%) · six shifts falling', // A PFS §7.1 (deck design says 32.0 oz/t)
    matchedRules: [{ ruleId: 'dr-pb-con-zn-contam', confidencePct: 78 }],
  },
  {
    id: 've-p80',
    subprocessId: 'sp-grind',
    variableId: 'mv-sa01-p80',
    detectedAt: '03:15',
    description: 'Cyclone overflow P80 coarser than design', // B: AI Performance finding 3
    magnitude: 'P80 82 µm vs 74 µm design · 12.8 vs 12.2 kWh/t', // A PFS design (deck design says 75 µm — MS-N25 closed)
    matchedRules: [{ ruleId: 'dr-regrind-coarse', confidencePct: 71 }],
  },
  {
    id: 've-cyc-wear',
    subprocessId: 'sp-grind',
    variableId: 'mv-sa01-p80',
    detectedAt: '06 Jun 08:00',
    description: 'Six-week cyclone overflow P80 drift with pump VSD rising at constant discharge pressure',
    magnitude: 'P80 74 → 81 µm over six weeks · pump VSD 62 → 71 %',
    matchedRules: [{ ruleId: 'dr-p80-apex', confidencePct: 76 }],
  },
  {
    id: 've-as',
    subprocessId: 'sp-pb',
    streamId: 'st-pbcon',
    detectedAt: '11:20', // B: AI Performance "Generated 08 Jun 2026 11:32" — moved before the 11:30 cursor (fixed clock, #302)
    description: 'Arsenic in Pb/Ag concentrate trending toward the smelter limit', // B: Impurities
    magnitude: 'As 0.32% vs limit 0.50% · +0.10% over 30 d · ~55 days to penalty', // B: Impurities
    matchedRules: [{ ruleId: 'dr-as-ore', confidencePct: 82 }],
  },
  {
    id: 've-cuso4',
    subprocessId: 'sp-zn',
    variableId: 'mv-reag-cuso4',
    detectedAt: '07:30',
    description: 'CuSO₄ FIT on setpoint with Zn recovery falling',
    magnitude: 'CuSO₄ dosing 155 g/t (design 150) · Zn recovery 81.2% vs design 85.1%',
    matchedRules: [{ ruleId: 'dr-cuso4', confidencePct: 75 }],
  },
];

export type ReportCasePin = {
  caseId: string;
  pinnedBy: 'gm';
  pinnedAt: string;
  provenance: string;
};

/** [syn] GM pins for the daily management report tracked-issue section (paul-tarp.A19). */
export const REPORT_CASE_PINS: readonly ReportCasePin[] = [
  {
    caseId: 'case-zn-recovery',
    pinnedBy: 'gm',
    pinnedAt: '2026-06-06T14:00',
    provenance: '[syn] GM tracked-issue pin for daily management report',
  },
];

// ---------------------------------------------------------------------------
// Cases — six positioned so every pipeline stage is occupied at 11:30
// (tim-scope advisory-loop §7), plus the five word-doc §6 worked examples.
// The hardness case stays in the ScenarioEngine (HARDNESS_CASE) — it is
// phase-driven, not a standing record.
// ---------------------------------------------------------------------------

const ADVISORY_CASES: readonly Case[] = [
  // --- the money path (CASE-001) -------------------------------------------
  {
    id: 'case-zn-recovery',
    code: 'ZN-REC',
    variationEventId: 've-zn-rec',
    title: 'Zinc recovery 2.8 pt below design', // derived at render; seed kept for non-journey surfaces
    circuit: 'Zn flotation',
    state: 'evidence-requested',
    branch: 'ask',
    assignedTo: 'metallurgist',
    severity: 'high',
    aiConfidencePct: 91, // B: AI Performance
    confidenceDiscount: {
      samplePointId: 'SA04', // Zn Rougher Conc 10:00 — S11 canonical MISS
      reason: 'Zn Rougher Conc 10:00 sample MISSED (operator on pH duty) — grade call discounted', // B: Sample Register; A §M1 §7
    },
    observation:
      'Recovery (24h avg) 82.3% vs design 85.1%. Zn circuit pH in-spec only 68% of this shift. CuSO₄ activation at 155 g/t (design 150) is adequate.', // day count derived in report (journey-fidelity C38)
    causeCandidate: 'lime · pH 68% in-spec', // B: AI Performance — lever + figure on glass
    ifThen:
      'IF circuit.zn_ph in-spec < 95% of shift AND reagent.smbs_actual > design THEN pH instability with SMBS carry-over co-cause — raise lime SP before changing collector.', // A §M3 hybrid; tim-scope DR-ZN-PH-01
    evidenceRequests: [
      { id: 'ev-smbs', kind: 'visual', target: 'operator', text: 'SMBS · check dosing', status: 'open' },
      { id: 'ev-lime-loop', kind: 'loop-state', target: 'cro', text: 'lime loop · AIC5002', status: 'open' },
    ],
    headline: {
      label: 'Recovery (24h avg)', // B: slide 05 finding 1
      value: '82.3%',
      basis: { label: 'Design', value: '85.1%' }, // S1 resolved — A PFS §7.1 (deck design column said 90.0%)
      delta: { text: '2.8 pt vs design', direction: 'down', tone: 'bad' },
      provenance: 'A PFS §7.1',
    },
    handler: { name: MET_HANDLER.name, role: 'metallurgist', since: '2026-06-08T10:30' }, // [syn] — tier 2 entry aligns with handler
    ladder: { ladderId: 'lad-zn-ph', tier: 2, enteredAt: '2026-06-08T10:30' },
    raisedAt: '2026-06-08T06:50',
  },
  // --- money path step 1: the alarm's zero-touch response (A §M3 branch 1) ---
  {
    id: 'case-zn-ph-response',
    code: 'ZN-PH-01',
    alarmId: 'alm-zn-ph',
    title: 'ZN-PH-01 — immediate response: increase lime SP',
    circuit: 'Zn flotation',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'cro',
    severity: 'high',
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — immediate lime response feeds zinc recovery.
    observation: 'Zn float pH 10.2 below SP band 10.8–11.2 at 06:42.', // B: Control Room; S7
    causeCandidate: 'Under-liming — the FMEA’s own Immediate Response Action attaches with no investigation step.',
    ifThen: 'IF Zn float pH < SP low limit THEN increase lime SP to Zn circuit — Immediate Response routed to the CRO (zero-touch).',
    controlModeCheck: { tag: 'AIC5002', mode: 'auto' },
    evidenceRequests: [],
    handler: { name: 'D. Okafor', role: 'cro', since: '06:43' }, // [syn] name + time
    raisedAt: '2026-06-08T06:42', // B: Ops Supervisor alarm list
    resolvedAt: '2026-06-08T06:55', // [syn]
  },
  // --- zinc rougher pH — the analytics-service case (zinc-case S1) ---
  {
    id: 'case-zn-rgh-ph',
    code: 'ZN-RGH-PH',
    // #758: name the case's own variable directly. The analytics-service case
    // has no seeded VariationEvent and no spawned alarm (the 06:42 ZN-PH-01
    // alarm spawned its own zero-touch `case-zn-ph-response`), so before this
    // link no variable resolved to it and Signals could not link it beside
    // ZN-REC on AT-150.
    variableId: 'mv-zn-ph',
    title: 'Rougher feed pH below limit',
    circuit: 'Zn flotation',
    state: 'matched',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'high',
    observation:
      'Rougher feed pH held below the Control Plan low limit through the day shift; the trend rule fired on the same tag.',
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — pH feeds the zinc recovery owner path.
    causeCandidate:
      'Under-liming at the rougher conditioner — candidate, pending confirmation of actual lime delivery.',
    ifThen:
      'IF rougher feed pH crosses its statistical lower control limit AND the trend is falling THEN verify actual lime flow, adjust the conditioner setpoint if delivery is low, and confirm AIC5002 in auto.',
    evidenceRequests: [],
    analysisRun: { fixture: 'zinc-rougher', primarySignalRole: 'rougher_feed_ph', expectationRole: 'rougher_recovery_proxy', recordedAgent: true },
    // [syn] K13 / reconciliation A4 (M3 worked example): recovery lags 2–4 h behind
    // the lime restored, and the case closes after two consecutive shift assays at
    // target — the same figures the lime-silo and Zn-recovery driver-tree issues carry.
    recovery: { lagHours: { min: 2, max: 4 }, closeAfterShiftAssays: 2 },
    // [syn] K13c — the case's diagnosis (INV-0001: AIC5002 left in manual after
    // the changeover) is what the living FMEA learns. The two lines ride with the
    // proposal as additions and go live only when the Senior met seat approves.
    learnedCause: 'lime loop left in manual after changeover',
    learnedEarlyWarning: 'AIC5002 in MAN > 30 min',
    diagnosedAt: '2026-06-08T09:30', // [syn] agent diagnosis lands after the committed replay frame
    raisedAt: '2026-06-07T03:04', // [syn] — zinc-case S1; clock from the analytics envelope
  },
  // --- CASE-002 -----------------------------------------------------------
  {
    id: 'case-ag-grade',
    code: 'AG-CONC',
    variationEventId: 've-ag-grade',
    alarmId: 'alm-ag-conc',
    title: 'Ag grade in Pb/Ag conc six shifts falling', // A PFS §7.1; K20 (#717) — the level is at design (12.14 vs 12.13), the case is the fall
    circuit: 'Pb/Ag concentrate',
    state: 'routed',
    branch: 'zero-touch',
    assignedTo: 'metallurgist',
    severity: 'high',
    aiConfidencePct: 78,
    observation: 'Ag 12.14 oz/t vs design 12.13 oz/t (+0.1%). Six shifts falling from 12.74.', // K20 (#717) — balance-implied level, the drift is the finding; round 1 dropped the closure clause (the case makes no closure claim)
    causeCandidate: '3418A · 42 g/t', // B: Metallurgist reagent consumption — lever + figure
    ifThen: 'IF 3418A < 45 g/t AND Ag in Pb/Ag conc falling for consecutive shifts THEN collector underdose — restore design 45 g/t before blaming mineralogy.',
    evidenceRequests: [],
    headline: {
      label: 'Ag in Pb/Ag Conc', // B: slide 05 finding 2
      value: '12.14 oz/t',
      basis: { label: 'Design', value: '12.13 oz/t' }, // S1 resolved — A PFS §7.1 (deck design says 32.0 oz/t)
      delta: { text: '0.1% vs design · six shifts falling', direction: AG_GRADE_DIRECTION, tone: 'warn' }, // K20 round 1 (#717) — direction derived from the grade's level vs design
      provenance: 'K20 (#717) — balance-implied head × recovery ÷ mass pull',
    },
    handler: { name: 'S. Park', role: 'metallurgist', since: '04:30' }, // [syn]
    respondBy: { at: '13:00', escalatesTo: 'superintendent' }, // [syn] (#55)
    raisedAt: '2026-06-08T04:10',
  },
  // --- CASE-003 -----------------------------------------------------------
  {
    id: 'case-p80',
    code: 'GRIND-P80',
    variationEventId: 've-p80',
    title: 'Grind P80 coarser than design', // B: AI Performance finding 3
    circuit: 'Ball mill / cyclones',
    state: 'routed',
    branch: 'zero-touch',
    assignedTo: 'maintenance',
    severity: 'med',
    aiConfidencePct: 71,
    observation: 'Cyclone overflow P80 82 µm vs design 74 µm. Specific energy 12.8 kWh/t vs 12.2.', // A PFS design (deck design says 75 µm — MS-N25 closed, seed-retie #258)
    causeCandidate: 'Cyclone feed pressure at the low end of SP (84 kPa / 82–92). Apex liner wear is a common early-startup issue.', // B: AI Performance
    ifThen: 'IF cyclone overflow P80 > 80 µm AND cyclone pressure at the low end of SP AND mill kWh/t rising THEN grind coarsening from apex wear / low pressure — not media depletion (kW/t would be falling).',
    evidenceRequests: [],
    headline: {
      label: 'Cyclone overflow P80', // B: slide 05 finding 3
      value: '82 µm',
      basis: { label: 'Design', value: '74 µm' }, // primary cyclone O/F; S2 is the regrind P80 sibling; MS-N25 closed (deck design says 75 µm)
      delta: { text: '10.8% vs design', direction: 'up', tone: 'bad' }, // V6: coarser is bad → red, though the deck draws ▲ green
      provenance: 'B: slide 05 · MS-N25 closed (seed-retie #258)',
    },
    handler: { name: 'K. Jones', role: 'maintenance', since: '08:15' }, // [syn] time; K. Jones = deck WO assignee (slide 11)
    respondBy: { at: '18:00', escalatesTo: 'superintendent' }, // [syn] (#55) — end of shift for a WEEK-urgency case
    raisedAt: '2026-06-08T03:15',
  },
  {
    id: 'case-cyc-wear',
    code: 'CYC-WEAR',
    variationEventId: 've-cyc-wear',
    title: 'Cyclone wear drift — six-week P80 and pump trend',
    circuit: 'Ball mill / cyclones',
    state: 'matched',
    branch: 'zero-touch',
    assignedTo: 'maintenance',
    severity: 'med',
    aiConfidencePct: 76,
    observation:
      'Cyclone overflow P80 drifted from 74 µm to 81 µm over six weeks. Cyclone feed pump VSD rose from 62 % to 71 % at constant discharge pressure.',
    causeCandidate: 'Apex and vortex-finder wear — cyclone inspection recommended before the cut goes coarser.',
    ifThen:
      'IF cyclone P80 rising-long AND pump VSD rising-long AND mill kW/t steady THEN cyclone pack wear — schedule apex inspection, not media depletion.',
    evidenceRequests: [],
    headline: {
      label: 'Cyclone overflow P80 (6 wk trend)',
      value: '81 µm',
      basis: { label: 'Six weeks ago', value: '74 µm' },
      delta: { text: '+7 µm over six weeks', direction: 'up', tone: 'bad' },
      provenance: '[syn] six-week daily series',
    },
    handler: { name: 'K. Jones', role: 'maintenance', since: '2026-06-06T08:00' },
    respondBy: { at: '2026-06-14T18:00', escalatesTo: 'superintendent' },
    affects: {
      readingId: 'rd-spec-p80',
      leadHours: 6 * 7 * 24,
      basis: 'FMEA dr-p80-apex — cyclone wear coarsens grind P80',
    },
    raisedAt: '2026-06-06T08:00',
  },
  // --- CASE-004 -----------------------------------------------------------
  {
    id: 'case-as-trend',
    code: 'AS-TREND',
    variationEventId: 've-as',
    title: 'Arsenic trending up in Pb/Ag concentrate', // B: AI Performance finding 4
    circuit: 'Pb/Ag concentrate',
    state: 'evidence-requested',
    branch: 'ask',
    assignedTo: 'superintendent',
    severity: 'monitor',
    aiConfidencePct: 82,
    provisional: { labDueAt: '12:40' }, // B: Impurities — "lab result due 12:40"; A §M1 §7 provisional → confirmed
    observation: 'As 0.32% today vs 0.22% 30 days ago. Smelter penalty threshold 0.50% in ~55 days at current rate.', // B: Impurities
    causeCandidate: 'No correlation with reagent changes — consistent with a gradual change in ore As as mining advances.', // B: AI Performance
    ifThen: 'IF As in Pb/Ag conc rising AND tails As stable AND no reagent step-change THEN ore-source As, not process — alert trading; do not chase with collector.',
    samplingArtefactCheck: {
      text: 'could be a sampling artefact · check: SA03 duplicate re-assay and cutter cycle',
      labDueAt: '12:40',
    },
    evidenceRequests: [
      {
        id: 'ev-rom-as',
        kind: 'lab-sample',
        target: 'operator',
        text: 'Collect ROM blast sample for As assay',
        status: 'open',
      },
      {
        id: 'ev-sa03-dup',
        kind: 'lab-sample',
        target: 'operator',
        text: 'SA03 duplicate re-assay and cutter cycle check',
        status: 'open',
      },
    ],
    headline: {
      label: 'As in Pb/Ag Conc (today)', // B: slide 05 finding 4
      value: '0.32%',
      basis: { label: '30d ago', value: '0.22%' }, // V7 — the deck's own basis; no As design value exists, the reference is the 0.50% smelter limit (slide 08)
      delta: { text: '+0.10% vs 30 d ago', direction: 'up', tone: 'bad' }, // V6 / V7: deck prints "vs design" in green; rising As is bad
      provenance: 'B: slide 05 · slide 08',
    },
    handler: { name: 'A. Chen', role: 'superintendent', since: '11:25' }, // [syn] — after raisedAt, before the 11:30 cursor
    respondBy: { at: '12:40', escalatesTo: 'metallurgist' }, // [syn] — ROM assay due 12:40 (tim-scope CASE-004)
    raisedAt: '2026-06-08T11:20', // fixed clock (#302): raised before the 11:30 cursor so the case is on the glass with a positive age
  },
  // --- CASE-005 — the gate, live in Approvals ------------------------------
  {
    id: 'case-smbs-fmea',
    code: 'FMR-SMBS',
    title: 'SMBS carry-over', // tim-scope advisory-loop §7 CASE-005
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — SMBS carry-over feeds zinc recovery.
    circuit: 'Zn flotation',
    state: 'fmea-review',
    branch: 'escalate',
    assignedTo: 'gm',
    severity: 'high',
    // aiConfidencePct intentionally absent — [syn] would be needed; omit the chip.
    observation: 'SMBS carry-over · Zn pH low', // sibling occurrence — figure on glass
    causeCandidate: 'SMBS · above design',
    ifThen: 'IF zn_ph in-spec % of shift < 95 AND reagent.smbs_actual > design THEN SMBS carry-over — proposed early-warning signature, pending Plant Manager approval.',
    evidenceRequests: [],
    handler: { name: 'L. Moreno', role: 'gm', since: '09:20' }, // [syn] — awaiting the single accountable gate
    respondBy: { at: '14:00', escalatesTo: 'gm' }, // [syn] (#55) — reminder to the same seat; the gate has no one above it
    raisedAt: '2026-06-06T14:20', // [syn]
  },
  // --- Recently closed — last two shifts before cursor (vision-run glass #315) -
  {
    id: 'case-feed-sp-closed',
    code: 'FEED-SP-CLR',
    title: 'Fresh-feed rate setpoint restored after brief dip',
    circuit: 'Grinding',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'cro',
    severity: 'low',
    observation: 'Fresh-feed tonnage dipped 3% below SP for 45 min on the day shift.', // [syn]
    causeCandidate: 'Operator trim — feed-rate SP restored without maintenance.',
    ifThen: 'IF feed rate < SP − 3% for > 30 min THEN restore SP — CRO Immediate Response.',
    evidenceRequests: [],
    handler: { name: 'D. Okafor', role: 'cro', since: '14:25' }, // [syn]
    raisedAt: '2026-06-05T14:20', // [syn] — CRO-scope event in 7-day window before since-last
    resolvedAt: '2026-06-05T14:55', // [syn]
  },
  {
    id: 'case-pb-ph-closed',
    code: 'PB-PH-CLR',
    title: 'Pb rougher pH excursion — lime SP raised',
    circuit: 'Pb flotation',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'cro',
    severity: 'med',
    observation: 'Pb rougher pH dipped below the 10.5–11.0 band for 40 min; lime loop AIC4010 stayed in Auto.',
    causeCandidate: 'Under-liming on the Pb circuit — Immediate Response lime SP bump cleared the excursion.',
    ifThen: 'IF Pb rougher pH < SP low THEN raise lime SP — routed to the CRO, no investigation step.',
    evidenceRequests: [],
    handler: { name: 'D. Okafor', role: 'cro', since: '22:15' }, // [syn]
    raisedAt: '2026-06-07T22:10', // [syn]
    resolvedAt: '2026-06-07T22:35', // [syn]
  },
  {
    id: 'case-cyclone-closed',
    code: 'CY-PRES-CLR',
    title: 'Cyclone feed pressure high — SP trimmed',
    circuit: 'Grinding',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'operator',
    severity: 'med',
    observation: 'Cyclone feed pressure touched 91 kPa against an 82–92 kPa SP for one hour overnight.',
    causeCandidate: 'Operator-held high pressure setpoint — trimmed back inside band without maintenance.',
    ifThen: 'IF cyclone feed pressure > SP high THEN trim SP — operator Immediate Response.',
    evidenceRequests: [],
    handler: { name: 'M. Reid', role: 'operator', since: '05:50' }, // [syn]
    raisedAt: '2026-06-08T05:20', // [syn]
    resolvedAt: '2026-06-08T05:45', // [syn]
  },
  {
    id: 'case-zn-cond-closed',
    code: 'ZN-COND-CLR',
    title: 'Zn conditioner pH low — lime and CuSO₄ adjusted',
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — conditioner pH feeds the zinc recovery path.
    circuit: 'Zn flotation',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'med',
    observation: 'Zn conditioner pH sat at 10.6 against the 10.8–11.2 band through the night shift.',
    causeCandidate: 'Under-liming with adequate CuSO₄ — both reagents stepped after metallurgy review.',
    ifThen: 'IF Zn conditioner pH < SP low AND CuSO₄ on setpoint THEN raise lime and confirm activator strength.',
    evidenceRequests: [],
    handler: { name: 'S. Park', role: 'metallurgist', since: '07:25' }, // [syn]
    raisedAt: '2026-06-07T22:10', // [syn] — metallurgist-closed within trailing 24 h at demo cursor
    resolvedAt: '2026-06-08T06:30', // [syn]
  },
  {
    id: 'case-mibc-closed',
    code: 'MIBC-CLR',
    title: 'Pb rougher frother overdose — MIBC trimmed',
    circuit: 'Pb flotation',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'low',
    feedNodeLabel: 'Pb recovery', // [syn] W1 r3 — frother control feeds lead recovery.
    observation: 'MIBC dosing ran 8% above design through the night shift with stable froth depth.',
    causeCandidate: 'Operator overshoot on the frother loop — metallurgy trimmed the setpoint at handover.',
    ifThen: 'IF MIBC actual > design + 5% AND froth depth stable THEN trim frother SP.',
    evidenceRequests: [],
    handler: { name: 'S. Park', role: 'metallurgist', since: '10:05' }, // [syn]
    raisedAt: '2026-06-08T06:45', // [syn] — metallurgist-closed within trailing 24 h at demo cursor
    resolvedAt: '2026-06-08T08:15', // [syn]
  },
  {
    id: 'case-cuso4-closed',
    code: 'CUSO4-CLR',
    title: 'Zn activator strength low — CuSO₄ dose stepped',
    circuit: 'Zn flotation',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'low',
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — CuSO₄ dose feeds zinc recovery.
    observation: 'CuSO₄ dosing sat 6% below design through the morning shift with stable conditioner pH.',
    causeCandidate: 'Activator strength drift — metallurgy stepped CuSO₄ after lab check.',
    ifThen: 'IF CuSO₄ actual < design − 5% AND conditioner pH stable THEN step activator dose.',
    evidenceRequests: [],
    handler: { name: 'S. Park', role: 'metallurgist', since: '09:40' }, // [syn]
    raisedAt: '2026-06-08T08:20', // [syn]
    resolvedAt: '2026-06-08T09:55', // [syn]
  },
  {
    id: 'case-lime-closed',
    code: 'LIME-CLR',
    title: 'Pb cleaner pH high — lime trimmed',
    feedNodeLabel: 'Pb cleaner pH', // [syn] W1 r3 — cleaner alkalinity is the corrected KPI.
    circuit: 'Pb flotation',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'low',
    observation: 'Pb cleaner pH touched 11.4 against the 10.8–11.2 band for 90 min on the day shift.',
    causeCandidate: 'Over-liming on the Pb circuit — lime SP trimmed after metallurgy review.',
    ifThen: 'IF Pb cleaner pH > SP high THEN trim lime SP — metallurgist review.',
    evidenceRequests: [],
    handler: { name: 'S. Park', role: 'metallurgist', since: '10:30' }, // [syn]
    raisedAt: '2026-06-08T09:10', // [syn]
    resolvedAt: '2026-06-08T10:45', // [syn]
  },
  {
    id: 'case-belt-closed',
    code: 'WIT-014-CLR',
    kind: 'zero-touch',
    title: 'Fresh-feed belt scale drift — zero-touch · no diagnosis',
    feedNodeLabel: 'Mill feed rate', // [syn] W1 r3 — belt calibration feeds measured mill feed.
    circuit: 'Plant-wide',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'maintenance',
    severity: 'low',
    observation: 'WIT-014 tonnage read 4.2% high vs the cross-check density meter for six hours.',
    causeCandidate: 'Instrument drift — maintenance recalibrated the belt scale; no process fault.',
    ifThen: 'IF belt tonnage > density cross-check + 3% for > 4 h THEN route calibration — zero-touch when instrument fault confirmed.',
    evidenceRequests: [],
    handler: { name: 'M. Reid', role: 'operator', since: '09:55' }, // [syn]
    raisedAt: '2026-06-08T08:05', // [syn]
    resolvedAt: '2026-06-08T09:50', // [syn]
  },
  // --- CASE-006 — Module 2 data-quality story, closed -----------------------
  {
    id: 'case-wit-021',
    code: 'WIT-021',
    title: 'WIT-021 single-anchor exposure', // A §M2 worked example
    circuit: 'Plant-wide',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'low',
    observation: 'WIT-021 is the sole fresh-feed tonnage anchor; all three circuits agree within 0.3%.', // A §M2 §5
    causeCandidate: 'Documented design-basis gap plus a single un-cross-checked belt scale — a data-quality finding, not a process fault.',
    ifThen: 'Informational — no rule match. Reachable from the mass-balance closure widget’s data-quality drawer.',
    evidenceRequests: [],
    raisedAt: '2026-06-06T14:00', // [syn] — outside trailing-24 h at demo cursor
    resolvedAt: '2026-06-06T15:00', // [syn]
  },
  // --- word-doc §6 worked examples (A §M3 6.1–6.5) ---------------------------
  {
    id: 'case-air',
    code: 'AIR-VALVE',
    kind: 'zero-touch',
    title: 'Zn recovery — air valve closed or blocked',
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — air availability feeds zinc flotation.
    circuit: 'Zn flotation',
    state: 'routed',
    branch: 'zero-touch',
    assignedTo: 'operator',
    severity: 'med',
    observation: 'Air FIT zero AND blower DI = running.', // A §M3 6.1
    causeCandidate: 'Air valve closed or blocked — no ambiguity, no competing cause, nothing to sample.',
    ifThen: 'IF air FIT zero AND blower DI = running THEN air valve closed or blocked — route Immediate Response to Operations, no investigation step.',
    evidenceRequests: [],
    nextStep: 'Check the Zn air valve', // [syn] P15 r1 — the operator's step for a zero-touch valve case
    handler: { name: 'M. Reid', role: 'operator', since: '05:12' }, // [syn]
    raisedAt: '2026-06-08T08:05', // [syn]
  },
  {
    id: 'case-cuso4',
    code: 'CUSO4-ACT',
    variationEventId: 've-cuso4',
    kind: 'lab-sample',
    title: 'Zn recovery — CuSO₄ activation ambiguity',
    feedNodeLabel: 'Zn recovery', // [syn] W1 r3 — activator diagnosis feeds zinc recovery.
    circuit: 'Zn flotation',
    state: 'evidence-requested',
    branch: 'ask',
    assignedTo: 'operator',
    severity: 'med',
    observation: 'CuSO₄ dosing FIT low or zero; LT-343 declining; AT-150 correct.', // A §M3 6.2
    causeCandidate: 'Solution degraded (titrate) vs a simple pump/valve fault if the FIT itself is low — the engine cannot tell these apart from DCS alone.',
    ifThen: 'IF CuSO₄ FIT on setpoint but Zn tails rising THEN solution degraded (titrate) — vs pump/valve fault if FIT itself is low.',
    evidenceRequests: [
      { id: 'ev-cuso4-titration', kind: 'lab-sample', target: 'cro', text: 'CRO to arrange CuSO₄ solution-strength titration; Metallurgy interprets.', status: 'open' },
    ],
    handler: { name: 'D. Okafor', role: 'cro', since: '2026-06-08T09:50' }, // [syn]
    ladder: { ladderId: 'lad-zn-cuso4', tier: 2, enteredAt: '2026-06-08T09:50' },
    raisedAt: '2026-06-08T07:30', // [syn]
  },
  {
    id: 'case-tramp',
    code: 'TRAMP-CV001',
    kind: 'visual',
    title: 'Hydrocone crusher — tramp iron (RPN 160)',
    feedNodeLabel: 'Crusher throughput', // [syn] W1 r3 — tramp iron threatens crusher throughput.
    circuit: 'Crushing',
    state: 'evidence-requested',
    branch: 'ask',
    assignedTo: 'maintenance',
    severity: 'med',
    observation: 'kW spike and rising bowl-relief count.', // A §M3 6.3
    causeCandidate: 'Tramp iron — a physical event; the recommendation is a location and a thing to check, not a data request.',
    ifThen: 'IF kW spike AND rising bowl-relief count THEN tramp iron — visual inspect the CV-001 magnet, review maintenance log for type and source.',
    evidenceRequests: [
      { id: 'ev-cv001-magnet', kind: 'visual', target: 'operator', text: 'Walk out to the CV-001 magnet and report what you see. Lab: none — physical event.', status: 'open' },
    ],
    handler: { name: 'M. Reid', role: 'operator', since: '10:20' }, // [syn]
    respondBy: { at: '11:20', escalatesTo: 'maintenance' }, // [syn] (#55) — walk-out to CV-001 magnet
    raisedAt: '2026-06-08T09:10', // [syn]
  },
  {
    id: 'case-lic',
    code: 'LIC4010',
    kind: 'control-mode',
    title: 'Pb rougher cell level high — loop in Manual',
    feedNodeLabel: 'Pb rougher cell level', // [syn] W1 r3 — manual loop affects rougher level.
    circuit: 'Pb flotation',
    state: 'outcome-documented',
    branch: 'zero-touch',
    assignedTo: 'operator',
    severity: 'low',
    observation: 'LT-140/141 high, dart valves ~100% open, reagents and air on setpoint.', // A §M3 6.4
    causeCandidate: 'Loop LIC4010 in Manual for 8.2 h — operator-held level, not an instrument or hydraulic fault. Track the control-mode case separately from a maintenance fault.',
    ifThen: 'IF LT-140/141 high AND reagents/air on SP AND loop in Manual THEN route as an operator-held control-mode case. Auto + valves at 100% would support the maintenance fault branch.',
    controlModeCheck: { tag: 'LIC4010', mode: 'manual', confirmedBy: 'cro', confirmedAt: '09:00' }, // CONTROL_LOOPS seed; [syn] time
    evidenceRequests: [],
    raisedAt: '2026-06-08T08:40', // [syn]
  },
  {
    id: 'case-gangue',
    code: 'GANGUE-NEW',
    kind: 'new-pattern',
    title: 'Both Pb + Zn grades declining slowly',
    feedNodeLabel: 'Pb + Zn concentrate grades', // [syn] W1 r3 — decline feeds concentrate grade.
    circuit: 'Plant-wide',
    state: 'fmea-review',
    branch: 'escalate',
    assignedTo: 'metallurgist',
    severity: 'med',
    observation: 'Pb+Zn grades · both −0.3%/d', // A §M3 6.5 — figure on glass
    causeCandidate: 'gangue · QEMSCAN shift',
    ifThen: 'IF both grades decline slowly AND all process signals on setpoint AND mine has advanced THEN check mineralogy before process trail (learned case — pending approval).',
    evidenceRequests: [],
    handler: { name: 'S. Park', role: 'metallurgist', since: '10:05' }, // [syn]
    diagnosedAt: '2026-06-08T10:05', // [syn] agent diagnosis follows the grade-shift signal
    raisedAt: '2026-06-04T06:00', // [syn]
  },
  // --- Month-six excursions for uptake decision history (vision-run uptake-seed #311) ---
  {
    id: 'case-pb-air-step',
    code: 'PB-AIR',
    title: 'Pb rougher air rate low — froth depth below band',
    circuit: 'Pb flotation',
    state: 'closed',
    branch: 'ask',
    assignedTo: 'ops-supervisor',
    severity: 'med',
    observation: 'Pb rougher air rate 6% below design with froth depth at the low end of band overnight.', // [syn]
    causeCandidate: 'Blower trim drift — operator air SP held conservative through the night shift.',
    ifThen: 'IF Pb rougher air < design − 5% AND froth depth low THEN step air rate before chasing frother.',
    evidenceRequests: [],
    handler: { name: 'D. Lee', role: 'ops-supervisor', since: '21:35' }, // [syn]
    raisedAt: '2026-06-07T21:30', // [syn]
    resolvedAt: '2026-06-08T01:02', // [syn] after the night-shift actions; uptake-history close
  },
  {
    id: 'case-cyc-density',
    code: 'CY-DENS',
    title: 'Cyclone feed density drift — SP trim overnight',
    circuit: 'Grinding',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'operator',
    severity: 'low',
    observation: 'Cyclone feed density ran 2.1% above SP for 90 min on the night shift.', // [syn]
    causeCandidate: 'Feed density drift — operator trim brought density back inside band.',
    ifThen: 'IF cyclone feed density > SP high for > 60 min THEN trim density SP — operator Immediate Response.',
    evidenceRequests: [],
    handler: { name: 'M. Reid', role: 'operator', since: '23:05' }, // [syn]
    raisedAt: '2026-06-07T23:00', // [syn]
    resolvedAt: '2026-06-08T02:15', // [syn] after the night-shift actions; uptake-history close
  },
  {
    id: 'case-rg-power',
    code: 'RG-PWR',
    title: 'Regrind mill power dip — load fell to 78%',
    feedNodeLabel: 'Regrind mill load', // [syn] W1 r3 — power dip feeds the regrind load KPI.
    circuit: 'Regrind',
    state: 'closed',
    branch: 'escalate',
    assignedTo: 'maintenance',
    severity: 'med',
    observation: 'Regrind mill load dipped to 78% for 25 min with feed rate steady.', // [syn]
    causeCandidate: 'Motor/inverter thermal or bearing drag — maintenance inspected before restart.',
    ifThen: 'IF regrind kW/load < 80% AND feed steady THEN inspect motor and coupling before returning to SP.',
    evidenceRequests: [],
    handler: { name: 'K. Jones', role: 'maintenance', since: '02:20' }, // [syn]
    raisedAt: '2026-06-08T02:15', // [syn]
    resolvedAt: '2026-06-08T06:45', // [syn] after the night-shift actions; uptake-history close
  },
  {
    id: 'case-thickener-bed',
    code: 'THK-BED',
    title: 'Thickener bed level rise — UF rate stepped',
    circuit: 'Thickening',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'metallurgist',
    severity: 'low',
    observation: 'Thickener bed level 12% above target with rake torque steady overnight.', // [syn]
    causeCandidate: 'Underflow restriction — metallurgy stepped UF rate to draw bed down.',
    ifThen: 'IF bed level > target + 10% AND rake torque steady THEN raise underflow rate.',
    evidenceRequests: [],
    handler: { name: 'S. Park', role: 'metallurgist', since: '23:55' }, // [syn]
    raisedAt: '2026-06-07T23:50', // [syn]
    resolvedAt: '2026-06-08T03:20', // [syn] after the night-shift actions; uptake-history close
  },
  {
    id: 'case-lime-trip',
    code: 'LIM-TRIP',
    title: 'Lime dosing pump trip — 8 min outage',
    circuit: 'Zn flotation',
    state: 'closed',
    branch: 'zero-touch',
    assignedTo: 'cro',
    severity: 'med',
    observation: 'Lime dosing pump LIM-402 tripped at 20:42; restarted 20:50.', // [syn]
    causeCandidate: 'Pump trip — Immediate Response restart with no investigation step.',
    ifThen: 'IF lime pump trip AND Zn pH falling THEN restart pump and verify loop in Auto.',
    evidenceRequests: [],
    handler: { name: 'D. Okafor', role: 'cro', since: '20:43' }, // [syn]
    raisedAt: '2026-06-07T20:45', // [syn]
    resolvedAt: '2026-06-07T23:30', // [syn] after the night-shift actions; uptake-history close
  },
  {
    id: 'case-belt-scale',
    code: 'WIT-DRIFT',
    title: 'Fresh-feed belt scale drift — cross-check watch',
    feedNodeLabel: 'Mill feed rate', // [syn] W1 r3 — scale cross-check is on the feed-rate anchor.
    circuit: 'Plant-wide',
    state: 'closed',
    branch: 'ask',
    assignedTo: 'ops-supervisor',
    severity: 'low',
    observation: 'WIT-014 read 2.8% high vs density cross-check for three hours.', // [syn]
    causeCandidate: 'Instrument drift — maintenance scheduled calibration before shift end.',
    ifThen: 'IF belt tonnage > density cross-check + 2% for > 2 h THEN route calibration.',
    evidenceRequests: [],
    handler: { name: 'K. Jones', role: 'maintenance', since: '09:35' }, // [syn]
    raisedAt: '2026-06-08T09:30', // [syn]
    resolvedAt: '2026-06-08T11:30', // [syn] after the day-shift cross-check actions
  },
  {
    id: 'case-bm-brg',
    analysisRun: { fixture: 'bm01-bearing', primarySignalRole: 'bm-bearing-temp', recordedAgent: false, milestonesFromFrames: true },
    stopPlan: {
      window: BM01_BEARING_PROPOSED_STOP,
      workOrderId: 'WO-441',
      humanCheck: 'Confirm lubrication, bearing clearance and alignment; verify the inspection crew and isolation plan for WO-441.', // [syn] Maintenance check before authorising the proposed inspection.
      closeCriterion: 'closes after inspection findings are recorded on WO-441 and the restarted bearing remains below the warning band for 24 h', // [syn] controlled-stop closure, not a claim of completion.
      // [syn] W3d handoff (equipment-beat comp Handoff A; reconciliation A6): the draft work order's fields,
      // the checks before release (issue spine iss-bm01-bearing: 14:00 vibration vs the 3.6 mm/s baseline,
      // oil pressure, 16:00 Fe, SKF 23148 kit, 20 % / 80 ppm release rule; A7 isolation permit) and the stop decision.
      workOrder: { asset: 'BM01 · trunnion bearing', criticality: 'Critical', kit: 'SKF 23148 bearing kit' },
      releaseChecks: [
        { at: '14:00', lead: 'Vibration check', detail: ', BM01 DE and NDE bearings', basis: 'vs 3.6 mm/s baseline' },
        { at: 'now', lead: 'Lube oil', detail: ' pressure ≥ 280 kPa and flow in both sight glasses' },
        { at: '16:00', lead: 'Oil sample Fe', detail: ' result from the lab' },
        { onStopDay: true, lead: 'Isolation permit', detail: ' for the BM01 drive before the stop' },
        { at: 'then', lead: 'Release', detail: ' if vibration > 20 % above baseline or Fe > 80 ppm' },
      ],
      stopDecision: { decides: 'GM', confirms: 'B. Fletcher · Maint Supt', due: 'today EOD' },
    },
    code: 'BM-BRG',
    alarmId: 'alm-bm-brg',
    title: 'Ball mill trunnion bearing temperature excursion',
    circuit: 'Ball mill',
    state: 'matched',
    branch: 'escalate',
    assignedTo: 'maintenance',
    severity: 'high',
    aiConfidencePct: 82,
    observation: `Ball mill trunnion bearing TE-622-625 at ${BM01_BEARING_READING.value!.toFixed(BM01_BEARING_READING.decimals)} °C, above the ${BM01_BEARING_READING.band!.hi} °C early-warning threshold (limit ${BM01_BEARING_READING.spec!.hi} °C).`,
    causeCandidate:
      `Trunnion bearing degradation — FMEA fm-trunnion warns of an unplanned mill stop above ${BM01_BEARING_READING.band!.hi} °C; plan before the ${BM01_BEARING_READING.spec!.hi} °C limit.`,
    ifThen: `IF bearing temp > ${BM01_BEARING_READING.band!.hi} °C warning for ${BM01_BEARING_TRIGGER.aboveWarningHours} h AND rising (post-restart slope > ${BM01_BEARING_TRIGGER.minimumPostRestartSlopeCPerDay} °C/day) THEN plan a stop before the projected ${BM01_BEARING_READING.spec!.hi} °C limit.`,
    evidenceRequests: [],
    headline: {
      label: 'Trunnion bearing temp',
      value: `${BM01_BEARING_READING.value!.toFixed(BM01_BEARING_READING.decimals)} °C`,
      basis: { label: 'Early warning', value: `${BM01_BEARING_READING.band!.hi} °C` },
      delta: { text: `+${(BM01_BEARING_READING.value! - BM01_BEARING_READING.band!.hi!).toFixed(BM01_BEARING_READING.decimals)} °C above warn`, direction: 'up', tone: 'bad' },
      provenance: '[syn] TE-622-625 at cursor',
    },
    handler: { name: 'J. Smith', role: 'maintenance', since: '03:20' },
    respondBy: { at: '15:30', escalatesTo: 'superintendent' },
    affects: {
      readingId: 'rd-kpi-mill-util',
      leadSource: 'bearing-limit-trend',
      basis: 'FMEA fm-trunnion — unplanned mill stop, utilisation loss',
    },
    raisedAt: '2026-06-08T03:15',
  },
];

export const CASES: readonly Case[] = ADVISORY_CASES;

export const ISSUE_THREADS: readonly IssueThread[] = [
  {
    id: 'thread-zn-recovery',
    title: 'Zn recovery / grade shortfall',
    caseIds: ['case-zn-recovery', 'case-cuso4', 'case-gangue', 'case-smbs-fmea'],
  },
  {
    id: 'thread-grinding-throughput',
    title: 'Grinding throughput gap',
    caseIds: ['case-p80', 'case-tramp', 'case-lic'],
  },
];

// ---------------------------------------------------------------------------
// Recommended actions — the deck's merged priority list, re-homed per case
// (B: AI Performance; "Generated 08 Jun 2026 11:32 · Next auto-refresh 12:00").
// Priority 1 comes from finding 2, which is why sequence lives here, not on Case.
// ---------------------------------------------------------------------------

// Decisions below name a SeatId (route-reads paul-feedback.A8 hard rule 1) —
// PRIMARY_SEAT_BY_ROLE: metallurgist -> seat-met, cro -> seat-cro,
// maintenance -> seat-maintenance (bunker-hill-org-seed.ts). ra-1 keeps its
// pre-#268 decision timestamp byte-identical: effectiveness-probe.mjs's Q5
// ladder and idle-state assertions (assertWatchedActionAligns,
// effectiveness-probe-entry.ts:81-104) pin case-ag-grade's `worsening` /
// `approved` read against it. ra-2's stamp is NOT probe-pinned — nothing in
// effectiveness-probe-entry.ts asserts on case-zn-recovery's decision time
// (uptake fixer round, MUST-FIX 3) — and it predated its own case's
// raisedAt (06:50) by 20 minutes, a seed contradiction rather than a
// constraint; moved to 07:10, 20 minutes after raisedAt. ra-1's stamp
// predates case-ag-grade's raisedAt too (04:10) and is a known, recorded
// limitation (docs/limitation.md) — it stays pinned, so it stays unfixed.
const ADVISORY_RECOMMENDED_ACTIONS: readonly RecommendedAction[] = [
  { id: 'ra-1', caseId: 'case-ag-grade', sequence: 1, urgency: 'now', text: 'Increase 3418A dosage to 45 g/t (Ag recovery gap)', status: 'approved', decision: { seat: 'seat-met', at: '2026-06-08T07:35' } },
  { id: 'ra-2', caseId: 'case-zn-recovery', sequence: 2, urgency: 'now', text: 'Increase lime to Zn circuit — target pH 11.0–11.2 (Zn recovery gap)', status: 'done', decision: { seat: 'seat-met', at: '2026-06-08T07:10' }, implementedAt: '2026-06-08T08:00' }, // [syn] implementedAt — 50 min after decision; decision.at moved 06:30 → 07:10 (uptake fixer round MUST-FIX 3): 20 min after case-zn-recovery's raisedAt (06:50), not probe-pinned
  { id: 'ra-3', caseId: 'case-as-trend', sequence: 3, urgency: 'today', text: 'Request As assays on ROM blast samples (arsenic trend)', status: 'proposed' },
  {
    id: 'ra-4',
    caseId: 'case-p80',
    sequence: 4,
    urgency: 'today',
    text: 'Increase cyclone feed pressure to 88–90 kPa (grind P80 gap)',
    status: 'rejected',
    // [syn] reason — tied to the case's own causeCandidate ("Cyclone feed pressure at the low end of SP (84 kPa / 82–92)"): 84 kPa is inside the SP band, not a breach, so raising it is premature ahead of the liner inspection (ra-5).
    // decision.at 05:45 — same night shift as the case's own raisedAt (03:15), ahead of the 06:00 day-shift handover (route-reads.uptake S4 both-shifts coverage).
    decision: { seat: 'seat-maintenance', at: '2026-06-08T05:45', reason: '84 kPa sits inside the 82–92 kPa SP band, not below it — not a pressure fault. Hold until the liner inspection (ra-5) confirms apex wear.' },
  },
  { id: 'ra-5', caseId: 'case-p80', sequence: 5, urgency: 'week', text: 'Schedule ball mill liner inspection (P80 + energy deviation)', status: 'proposed' },
  {
    id: 'ra-cyc-wear',
    caseId: 'case-cyc-wear',
    sequence: 1,
    urgency: 'week',
    text: 'Cyclone inspection — apex and vortex finder',
    proposedAt: '2026-06-06T08:30', // [syn] proposal follows the wear case raise
    status: 'proposed',
  },
  { id: 'ra-6', caseId: 'case-ag-grade', sequence: 6, urgency: 'week', text: 'Calibrate Pb/Ag thickener density instrument (Ag mass balance)', status: 'proposed' },
  // Per-finding secondary actions (B: AI Performance finding 1 actions 2–3), not on the merged list
  { id: 'ra-zn-2', caseId: 'case-zn-recovery', sequence: null, urgency: 'today', text: 'Review Zn conditioner residence time', status: 'proposed' },
  { id: 'ra-zn-3', caseId: 'case-zn-recovery', sequence: null, urgency: 'today', text: 'Check SMBS dosage in Pb circuit for excess carry-over', status: 'proposed' },
  // route-reads paul-feedback.A8 S4 (#268/#279 coverage) — the zero-touch
  // Immediate Response Action on case-zn-ph-response (assignedTo: 'cro'):
  // Paul named the control room operator first, and the seed otherwise has
  // no recommendation routed to that role at all. ifThen already states the
  // action; resolvedAt (06:55) is the case's own record of when it closed —
  // implementedAt mirrors it, decision.at a minute after raisedAt (06:42).
  { id: 'ra-zn-ph-1', caseId: 'case-zn-ph-response', sequence: null, urgency: 'now', text: 'Increase lime SP to Zn circuit — Immediate Response Action (zero-touch)', status: 'done', decision: { seat: 'seat-cro', at: '2026-06-08T06:44' }, implementedAt: '2026-06-08T06:55' },
  {
    id: 'ra-feed-sp-1',
    caseId: 'case-feed-sp-closed',
    sequence: null,
    urgency: 'now',
    text: 'Restore fresh-feed rate SP to 210 t/h',
    status: 'done',
    decision: { seat: 'seat-cro', at: '2026-06-05T14:22' }, // [syn] — CRO-scope decision in 7-day gap
    implementedAt: '2026-06-05T14:55',
  },
  { id: 'ra-gangue-1', caseId: 'case-gangue', sequence: null, urgency: 'week', text: 'QEMSCAN · compare block', status: 'proposed', proposedAt: '2026-06-08T10:15' }, // [syn]
  { id: 'ra-smbs-1', caseId: 'case-smbs-fmea', sequence: null, urgency: 'today', text: 'FMEA row · SMBS carry-over', status: 'proposed', proposedAt: '2026-06-08T09:20' }, // [syn]
  { id: 'ra-zn-rgh-ph', caseId: 'case-zn-rgh-ph', sequence: null, urgency: 'now', text: 'Verify actual lime flow, adjust the conditioner setpoint if delivery is low, and confirm AIC5002 in auto', status: 'proposed', proposedAt: '2026-06-08T10:00' }, // [syn] zinc-case S3 — the THEN clause of the case's ifThen, sentence-cased
];

export const RECOMMENDED_ACTIONS: readonly RecommendedAction[] = [
  ...ADVISORY_RECOMMENDED_ACTIONS,
  ...UPTAKE_HISTORY_ACTIONS,
];

// ---------------------------------------------------------------------------
// Case outcomes — always a PROPOSED change, never live (A §M3 §3).
// ---------------------------------------------------------------------------

export const CASE_OUTCOMES: readonly CaseOutcome[] = [
  {
    id: 'co-smbs',
    caseId: 'case-smbs-fmea',
    diagnosedCause: 'SMBS carry-over from the Pb circuit suppressing CuSO₄ activation in the Zn conditioner',
    causeCategory: 'metallurgical-process',
    evidence: 'pH in-spec % of shift < 95 sustained; SMBS actual above design; Zn rougher tails assay elevated',
    actionTaken: 'Lime SP raised to Zn circuit; SMBS dosing in Pb circuit checked and trimmed',
    effectiveness: 'Recovery response pending — sibling occurrence still open (case-zn-recovery)',
    proposedRuleId: 'dr-zn-ph',
  },
  {
    id: 'co-lic',
    caseId: 'case-lic',
    diagnosedCause: 'Operator-held level — LIC4010 in Manual; no equipment fault',
    causeCategory: 'mechanical-electrical', // [syn] A §M3 §4 — dart valve / level instrument is mech/E/I routing
    evidence: 'ControlLoopState LIC4010 = manual, 8.2 h; dart valves ~100% open',
    actionTaken: 'None required — nothing to propose beyond the control-mode gap in the existing rule',
    proposedRuleId: 'dr-dart-valve',
  },
  {
    id: 'co-zn-ph-response',
    caseId: 'case-zn-ph-response',
    diagnosedCause: 'Under-liming — Zn float pH below SP band',
    causeCategory: 'metallurgical-process',
    evidence: 'Zn float pH 10.2 at 06:42; lime loop AIC5002 in Auto',
    actionTaken: 'Lime SP raised to the Zn circuit',
  },
  {
    id: 'co-pb-ph-closed',
    caseId: 'case-pb-ph-closed',
    diagnosedCause: 'Under-liming on the Pb circuit',
    causeCategory: 'metallurgical-process',
    evidence: 'Pb rougher pH below 10.5 for 40 min; lime loop in Auto',
    actionTaken: 'Lime SP raised on the Pb circuit',
  },
  {
    id: 'co-feed-sp-closed',
    caseId: 'case-feed-sp-closed',
    diagnosedCause: 'Feed-rate SP held low after handover',
    causeCategory: 'mechanical-electrical',
    evidence: 'WIT-014 read 3% below SP for 45 min',
    actionTaken: 'Feed-rate SP restored to design',
  },
  {
    id: 'co-cyclone-closed',
    caseId: 'case-cyclone-closed',
    diagnosedCause: 'Operator-held high cyclone feed pressure',
    causeCategory: 'mechanical-electrical',
    evidence: 'Pressure at 91 kPa against 82–92 kPa SP for one hour',
    actionTaken: 'Operator trimmed cyclone feed pressure SP back inside band',
  },
  {
    id: 'co-zn-cond-closed',
    caseId: 'case-zn-cond-closed',
    diagnosedCause: 'Under-liming on the Zn conditioner with adequate CuSO₄',
    causeCategory: 'metallurgical-process',
    evidence: 'Zn conditioner pH 10.6 vs 10.8–11.2 band overnight',
    actionTaken: 'Lime and CuSO₄ dosing stepped on the Zn conditioner',
  },
  {
    id: 'co-mibc-closed',
    caseId: 'case-mibc-closed',
    diagnosedCause: 'Frother overdose on the Pb rougher',
    causeCategory: 'metallurgical-process',
    evidence: 'MIBC 8% above design with stable froth depth',
    actionTaken: 'MIBC setpoint trimmed at metallurgy handover',
  },
  {
    id: 'co-belt-closed',
    caseId: 'case-belt-closed',
    diagnosedCause: 'Belt scale drift — instrument fault, not ore or flow',
    causeCategory: 'mechanical-electrical',
    evidence: 'WIT-014 4.2% high vs density cross-check for six hours',
    actionTaken: 'WIT-014 recalibrated — instrument fault, no process change',
  },
  {
    id: 'co-wit-021',
    caseId: 'case-wit-021',
    diagnosedCause: 'Single tonnage anchor (WIT-021) with no independent cross-check; documented Pb design-basis gap',
    causeCategory: 'feed-ore',
    evidence: 'Mass-balance workbook: all three circuits agree within 0.3%',
    actionTaken: 'Logged as a data-quality finding; standing mass-balance deliverable requested from client',
  },
  {
    id: 'co-ag-grade',
    caseId: 'case-ag-grade',
    diagnosedCause: '3418A collector underdose — Ag grade falling in Pb/Ag conc',
    causeCategory: 'metallurgical-process',
    evidence:
      'Ag 12.14 oz/t at design 12.13 (+0.1%), six shifts falling; FIT-3418A below design with Ag in Pb/Ag conc lagging for three shifts',
    actionTaken: 'Increase 3418A dosage to 45 g/t; calibrate Pb/Ag thickener density instrument',
    proposedRuleId: 'dr-ag-3418a',
  },
  {
    id: 'co-gangue',
    caseId: 'case-gangue',
    diagnosedCause: 'Gangue mineral association shift — specific mine block, not grind/reagent',
    causeCategory: 'feed-ore',
    evidence: 'All reagent, pH and PSA signals on setpoint throughout the decline; QEMSCAN confirms increased gangue locking vs baseline mineralogy',
    actionTaken: 'Cross-check mine block against QEMSCAN library before assuming grind/reagent fault',
    proposedRuleId: 'dr-mineralogy',
  },
  // --- rules2 (paul-email.A5 / paul-call.A5-A6, #250) -----------------------
  // No originating Case for these seven — they are proposed straight from
  // Paul's cause lists (corpus), not learned from a live incident. caseId
  // names a synthetic reference; Approvals.transform's enrichFmeaReview
  // resolves caseItem to undefined for these and the drawer renders the
  // outcome/rule sections only (Scene.tsx already guards caseItem? — no
  // crash, no blank claim of a source case).
  {
    id: 'co-media-size',
    caseId: 'case-paul-a5-media-size',
    diagnosedCause: 'Media size distribution wrong for the ore',
    causeCategory: 'mechanical-electrical',
    evidence: "No live case — proposed from Paul's 2026-09-04 email grinding-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-media-size',
  },
  {
    id: 'co-grate-wear',
    caseId: 'case-paul-a5-grate-wear',
    diagnosedCause: 'Mill discharge grates worn or blocked',
    causeCategory: 'mechanical-electrical',
    evidence: "No live case — proposed from Paul's 2026-09-04 email grinding-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-grate-wear',
  },
  {
    id: 'co-belt-weigher',
    caseId: 'case-paul-a5-belt-weigher',
    diagnosedCause: 'Inaccurate belt weigher overloading the mill',
    causeCategory: 'mechanical-electrical',
    evidence: "No live case — proposed from Paul's 2026-09-04 email grinding-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-belt-weigher',
  },
  {
    id: 'co-air-flow-pb',
    caseId: 'case-paul-a5-air-flow-pb',
    diagnosedCause: 'Pb rougher air flow too high or too low',
    causeCategory: 'metallurgical-process',
    evidence: "No live case — proposed from Paul's 2026-09-04 email flotation-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-air-flow-pb',
  },
  {
    id: 'co-air-flow-zn',
    caseId: 'case-paul-a5-air-flow-zn',
    diagnosedCause: 'Zn rougher air flow too high or too low',
    causeCategory: 'metallurgical-process',
    evidence: "No live case — proposed from Paul's 2026-09-04 email flotation-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-air-flow-zn',
  },
  {
    id: 'co-froth-height-pb',
    caseId: 'case-paul-a5-froth-height-pb',
    diagnosedCause: 'Pb rougher froth height too low',
    causeCategory: 'metallurgical-process',
    evidence: "No live case — proposed from Paul's 2026-09-04 email flotation-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-froth-height-pb',
  },
  {
    id: 'co-froth-height-zn',
    caseId: 'case-paul-a5-froth-height-zn',
    diagnosedCause: 'Zn rougher froth height too low',
    causeCategory: 'metallurgical-process',
    evidence: "No live case — proposed from Paul's 2026-09-04 email flotation-cause list (#250)",
    actionTaken: 'Logged as a proposed FMEA row and structured rule pending Manager sign-off',
    proposedRuleId: 'dr-froth-height-zn',
  },
  // --- paper-sourced (paul-fmea.A7, #274) -----------------------------------
  // No originating Case — this outcome is a literature ingest, not a live
  // incident (co-media-size and siblings above are the case-shaped precedent
  // for a synthetic caseId with no CASES row). `proposedRuleId` is
  // deliberately absent: bunker-hill-rule-claims.ts builds the paper's claim
  // directly as a `document`-tier claim (paperClaim()), not through the
  // generic case-claim pipeline this field feeds — setting it here would
  // make caseClaims() mint a second, duplicate `case`-tier claim for the
  // same rule id.
  {
    id: 'co-paper-sb-bi',
    caseId: 'case-paper-sb-bi',
    diagnosedCause: 'Sb/Bi sulfosalts co-floating with galena, undepressed — no dedicated depressant stage (MD-054)',
    causeCategory: 'metallurgical-process',
    evidence: 'Smith, Bruckard & Sparrow (2022), Mineral Processing and Extractive Metallurgy 131(3), DOI 10.1080/25726641.2021.1967079 — literature review, not a plant sample',
    actionTaken: 'Logged as a proposed FMEA row pending Manager sign-off; a paper never goes live on ingest alone',
  },
  {
    id: 'co-zone-band-zn-rec',
    caseId: 'case-zone-band-zn-rec',
    diagnosedCause: 'High-silver zone feed needs its own Zn recovery band — Main stope band no longer applies',
    causeCategory: 'metallurgical-process',
    evidence: 'Ore zone change 05 Jun 18:00 — feed switched from Main stope to High-silver zone',
    actionTaken: 'Proposed band change routed to senior metallurgist review',
  },
];

const ZN_AG_BAND_PROPOSAL = proposedZnRecBandForAgZone();

// ---------------------------------------------------------------------------
// FMEA update reviews — the gate. Reviewer sense-checks; ONE approver signs.
// ---------------------------------------------------------------------------

export const FMEA_UPDATE_REVIEWS: readonly FMEAUpdateReview[] = [
  {
    id: 'FMR-0007',
    caseOutcomeId: 'co-smbs',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '2026-06-08T09:15', // [syn]
    reviewNotes: 'IF→THEN holds; likelihood high; routing to Metallurgy correct. Will not misfire on a plain lime-loop upset because the SMBS clause discriminates.',
    reviewStatus: 'approved',
    approvalStatus: 'pending', // the GM/Manager persona holds fmea:approve (reconciliation R10)
    proposedChange: { kind: 'new-row', summary: 'Early-warning signature: pH-in-spec % of shift < 95 AND SMBS actual > design → SMBS carry-over', ruleId: 'dr-zn-ph' },
  },
  {
    id: 'FMR-0008',
    caseOutcomeId: 'co-gangue',
    causeCategory: 'feed-ore',
    reviewSeats: ['senior-metallurgist'], // A §M3 6.5 — feed/ore logged as a combination review, diagnostic content to Senior Met
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '2026-06-08T10:10', // [syn]
    reviewNotes: 'Ore mineralogy step-change signature holds when all process variables are on setpoint — PSA on-target rules out grind.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Both Pb + Zn concentrate grade declining (slow) → gangue association shift; check mineralogy before process trail', ruleId: 'dr-mineralogy' },
  },
  {
    id: 'FMR-0006',
    caseOutcomeId: 'co-lic',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '2026-06-08T09:00', // [syn] W1 r4 — review follows the LIC4010 case raised at 08:40.
    reviewNotes: 'After the LIC4010 manual-held case, adds controlMode = auto to the cell-level trigger so the automatic fault row fires only when the loop is in AUTO.',
    reviewStatus: 'approved',
    approvalStatus: 'approved',
    approvedBy: APPROVAL_GATE_SEAT, // B: Field Operator contacts; named individual not documented (A §M3 §7)
    approvedAt: '2026-06-08T09:15', // [syn] W1 r4 — approved after the 08:40 case raise.
    proposedChange: { kind: 'narrower-condition', summary: 'Cell-level fault trigger requires LIC4010 in AUTO; the manual-held level stays on its operator case', ruleId: 'dr-dart-valve' },
  },
  {
    id: 'FMR-0009',
    caseOutcomeId: 'co-lic',
    causeCategory: 'mechanical-electrical', // [syn] A §M3 §4 — mech/E/I review seat
    reviewSeats: ['senior-reliability-engineer'],
    reviewStatus: 'pending',
    approvalStatus: 'pending',
    proposedChange: {
      kind: 'new-row',
      summary: 'LIC4010 dart-valve actuator and level instrument — inspection row when loop in AUTO and valves at 100%',
      ruleId: 'dr-dart-valve',
    },
  },
  // --- rules2 (paul-email.A5 / paul-call.A5-A6, #250) -----------------------
  // Reviewer = Metallurgist per Q3, senior-metallurgist / senior-reliability-
  // engineer seat still routed by causeCategory (existing convention); a
  // review taken at the senior-metallurgist seat names that seat's holder
  // (SENIOR_MET_REVIEWER, discrepancies row 317). Every
  // row: reviewStatus approved (reviewer recommends), approvalStatus pending
  // (awaiting the Manager's sign-off) — the FMR-0007 shape, so these land in
  // the Approvals 'to-review' tab and count in the proposed chip without
  // being live (RuleSetEngine.liveRuleSet only promotes a claim on an
  // approval ruling; none exists here at seed).
  {
    id: 'FMR-0010',
    caseOutcomeId: 'co-media-size',
    causeCategory: 'mechanical-electrical',
    reviewSeats: ['senior-reliability-engineer'],
    reviewedBy: 'S. Park',
    reviewedAt: '09:30', // [syn]
    reviewNotes: 'No rule covered media size distribution as a grinding cause (paul-email.A5, #250). Structured on SA01 rising with kW/tph steady — reads distinct from dr-media and dr-hardness.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Media size distribution wrong for the ore — PSA coarsens with kW/tph steady', ruleId: 'dr-media-size' },
  },
  {
    id: 'FMR-0011',
    caseOutcomeId: 'co-grate-wear',
    causeCategory: 'mechanical-electrical',
    reviewSeats: ['senior-reliability-engineer'],
    reviewedBy: 'S. Park',
    reviewedAt: '09:35', // [syn]
    reviewNotes: 'No rule covered grate wear/blockage as a grinding cause (paul-email.A5, #250). Discriminates from dr-p80-apex on cyclone O/F density, not pressure.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Mill discharge grates worn or blocked — PSA coarsens with cyclone O/F density falling', ruleId: 'dr-grate-wear' },
  },
  {
    id: 'FMR-0012',
    caseOutcomeId: 'co-belt-weigher',
    causeCategory: 'mechanical-electrical',
    reviewSeats: ['senior-reliability-engineer'],
    reviewedBy: 'S. Park',
    reviewedAt: '09:40', // [syn]
    reviewNotes: 'No rule covered belt-weigher error as a grinding cause (paul-email.A5, #250). WIT-021 reading steady is part of the signature, not a contradiction — the loop holds the bad reading on setpoint.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Inaccurate belt weigher overloading the mill — kW/tph rising with WIT-021 steady', ruleId: 'dr-belt-weigher' },
  },
  {
    id: 'FMR-0013',
    caseOutcomeId: 'co-air-flow-pb',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '09:45', // [syn]
    reviewNotes: 'No rule covered air flow as a flotation cause (paul-call.A5, #250). Split by circuit — a rule cannot span sp-pb and sp-zn.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Pb rougher air flow off design — recovery falls either direction', ruleId: 'dr-air-flow-pb' },
  },
  {
    id: 'FMR-0014',
    caseOutcomeId: 'co-air-flow-zn',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '09:50', // [syn]
    reviewNotes: 'No rule covered air flow as a flotation cause (paul-call.A5, #250). Zn-circuit sibling of FMR-0013.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Zn rougher air flow off design — recovery falls either direction', ruleId: 'dr-air-flow-zn' },
  },
  {
    id: 'FMR-0015',
    caseOutcomeId: 'co-froth-height-pb',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '09:55', // [syn]
    reviewNotes: 'No rule covered froth height as a flotation cause (paul-email.A5, #250). Split by circuit for the same reason as air flow.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Pb rougher froth height too low — grade rises as recovery falls', ruleId: 'dr-froth-height-pb' },
  },
  {
    id: 'FMR-0016',
    caseOutcomeId: 'co-froth-height-zn',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '10:00', // [syn]
    reviewNotes: 'No rule covered froth height as a flotation cause (paul-email.A5, #250). Zn-circuit sibling of FMR-0015.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Zn rougher froth height too low — grade rises as recovery falls', ruleId: 'dr-froth-height-zn' },
  },
  // --- paper-sourced (paul-fmea.A7, #274) -----------------------------------
  {
    id: 'FMR-0017',
    caseOutcomeId: 'co-paper-sb-bi',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '10:10', // [syn]
    reviewNotes: 'No workbook row covers Sb/Bi depression staging (MD-054 is an open gap in the Concentrate Penalty Elements section). Literature review, not a plant sample — recommend trialling before adding a reagent stage.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: { kind: 'new-row', summary: 'Sb/Bi sulfosalt depression — pH/pulp-potential or starvation collector dosing per Smith, Bruckard & Sparrow (2022)', ruleId: 'dr-sb-bi-depression' },
  },
  // --- control-plan band change (paul-tarp.A10, #412) -----------------------
  {
    id: 'FMR-0018',
    caseOutcomeId: 'co-zone-band-zn-rec',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewStatus: 'pending',
    approvalStatus: 'pending',
    proposedChange: {
      kind: 'band-change',
      summary: `Zone change 05 Jun 18:00 — propose Zn recovery ≥ ${ZN_AG_BAND_PROPOSAL.band.lo!.toFixed(1)} % for High-silver zone feed`,
      bandChange: {
        controlPlanRowId: 'cp-zn-rec',
        fromZoneId: 'oz-main',
        toZoneId: 'oz-ag',
        proposedBand: ZN_AG_BAND_PROPOSAL.band,
        basis: ZN_AG_BAND_PROPOSAL.basis,
      },
    },
  },
  // persona-split MF8 — senior-met stewardship pending reviews at cursor [syn]
  {
    id: 'FMR-0019',
    caseOutcomeId: 'co-ag-grade',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewStatus: 'pending',
    approvalStatus: 'pending',
    proposedChange: {
      kind: 'new-row',
      summary: '3418A collector underdose — FIT-3418A below design with Ag in Pb/Ag conc falling for three shifts', // K20 round 1 (#717) — falling-trend premise
      ruleId: 'dr-3418a',
    },
  },
  {
    id: 'FMR-0020',
    caseOutcomeId: 'co-air-flow-zn',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    reviewStatus: 'pending',
    approvalStatus: 'pending',
    proposedChange: {
      kind: 'narrower-condition',
      summary: 'Zn pH low signature — AT-Zn-pH below 10.8 for more than twenty minutes of shift with recovery lag',
      ruleId: 'dr-zn-ph',
    },
  },
  {
    id: 'FMR-0021',
    caseOutcomeId: 'co-gangue',
    causeCategory: 'feed-ore',
    reviewSeats: ['senior-metallurgist'],
    reviewStatus: 'pending',
    approvalStatus: 'pending',
    proposedChange: {
      kind: 'new-row',
      summary: 'Ore hardness step-change — kW/tph rising with P80 steady and no media-size rule firing',
      ruleId: 'dr-hardness',
    },
  },
  // --- Capacity strategy C — M12 §7 / paul-v2.A7 ---------------------------
  {
    id: 'FMR-0022',
    causeCategory: 'metallurgical-process',
    reviewSeats: ['senior-metallurgist'],
    approvalPersonas: ['Metallurgist', 'Senior metallurgist'],
    approvalLabel: 'Metallurgist or Senior Metallurgist',
    reviewedBy: SENIOR_MET_REVIEWER, // org seed holder of seat-senior-met (P27 r1, row 317)
    reviewedAt: '08 Jun 10:45', // [syn] reviewed on the demo cursor
    reviewNotes: 'The price-sensitive Pb/Ag recipe trades higher lead and silver recovery against Zn circuit recovery and concentrate quality.',
    reviewStatus: 'approved',
    approvalStatus: 'pending',
    proposedChange: {
      kind: 'new-row',
      summary: 'Strategy C — approve ZnSO₄ at 350 g/t in the Pb circuit, down from 500 g/t',
    },
  },
];

// ---------------------------------------------------------------------------
// Failure modes — successor to FMEA_ROWS, typed against unit-op TYPES.
// Inline seed — kept in sync with FAILURE_MODES below.
// ---------------------------------------------------------------------------

export const FAILURE_MODES: readonly FailureMode[] = [
  {
    id: 'fm-zn-ph',
    unitOpType: 'flotation',
    circuit: 'Zn flotation',
    failureMode: 'pH drift / under-lime',
    cause: 'Lime SP too low; conditioner residence short; SMBS carry-over',
    effect: 'Zn recovery falls below ramp target; activation suppressed',
    s: 8,
    o: 7,
    d: 4,
    rpn: 224,
    earlyWarning: 'AT-Zn-pH < 10.8 for >20 min of shift; recovery lag vs 8-day baseline',
    status: 'proposed',
    outcome: 'recovery',
  },
  {
    id: 'fm-3418a',
    unitOpType: 'flotation',
    circuit: 'Pb circuit',
    failureMode: 'Collector underdose (3418A)',
    cause: 'Pump calibration drift; inventory setpoint left at commissioning 42 g/t',
    effect: 'Ag reports away from Pb/Ag conc; grade falling for consecutive shifts toward design', // K20 round 1 (#717) — falling-trend premise
    s: 7,
    o: 6,
    d: 3,
    rpn: 126,
    earlyWarning: 'FIT-3418A < 45 g/t AND Ag in Pb/Ag conc falling for 3 shifts', // K20 round 1 (#717) — falling-trend premise
    status: 'proposed',
    outcome: 'grade',
  },
  {
    id: 'fm-apex',
    unitOpType: 'cyclone',
    circuit: 'Classification',
    failureMode: 'Cyclone apex liner wear',
    cause: 'Startup wear faster than design; pressure held at bottom of SP band',
    effect: 'P80 coarsens; Zn and Pb liberation suffer',
    s: 6,
    o: 7,
    d: 5,
    rpn: 210,
    earlyWarning: 'Cyclone pressure < 85 kPa AND P80 > 80 µm AND mill kWh/t rising',
    status: 'live',
    outcome: 'both',
  },
  {
    id: 'fm-trunnion',
    unitOpType: 'ball_mill',
    circuit: 'Grinding',
    failureMode: 'Trunnion bearing temperature rise',
    cause: 'Lubrication / alignment; elevated power draw on a new mill',
    effect: 'Unplanned mill stop; utilisation loss',
    s: 9,
    o: 4,
    d: 3,
    rpn: 108,
    earlyWarning: 'TE-622-625 > 80°C (limit 85°C) with power draw above design',
    status: 'proposed',
  },
  {
    id: 'fm-as',
    unitOpType: 'flotation',
    circuit: 'Pb rougher',
    failureMode: 'Arsenopyrite reporting to Pb/Ag conc',
    cause: 'Ore As rising; Pb rougher pH 8.4 floats As',
    effect: 'Smelter penalty at 0.50% As',
    s: 7,
    o: 5,
    d: 4,
    rpn: 140,
    earlyWarning: 'As in Pb/Ag conc rising >0.003%/day with tails As stable',
    status: 'proposed',
    outcome: 'grade',
  },
  {
    id: 'fm-gangue',
    unitOpType: 'plant-wide',
    circuit: 'Plant-wide',
    failureMode: 'Gangue mineral association shift (mine block)',
    cause: 'Specific mine block — not grind or reagent. Learned case, pending approval.',
    effect: 'Both Pb and Zn concentrate grades decline slowly with all process signals on setpoint',
    s: 6,
    o: 3,
    d: 8,
    rpn: 144,
    earlyWarning:
      'Both grades declining slowly AND all reagent/pH/PSA on setpoint AND mine has advanced',
    status: 'proposed',
    outcome: 'grade',
  },
  // --- rules2 (paul-email.A5 / paul-call.A5-A6, #250) -----------------------
  // Five new FMEA rows for the grinding + flotation causes on Paul's two
  // cause lists that had no rule (docs/engines/diagnosis.md 'Coverage' table).
  // status: 'proposed' throughout -- these enter the sign-off queue via
  // FMR-0010..FMR-0016, they do not go live at t0 (rules2-probe.mjs asserts
  // live count unchanged from before this stream).
  {
    id: 'fm-media-size',
    unitOpType: 'ball_mill',
    circuit: 'Grinding',
    failureMode: 'Ball charge media size distribution wrong for the ore',
    cause: 'Recharge sized off the wrong ball-scats curve; undersize media added at the last reline',
    effect: 'PSA coarsens with kW/tph steady -- distinct from media wear (kW/tph falls) and hardness (kW/tph rises)',
    s: 6,
    o: 4,
    d: 6,
    rpn: 144, // [syn] -- no FMEA workbook row for this cause; S/O/D estimated in the RPN band of the sibling grinding rows (fm-apex, fm-trunnion)
    earlyWarning: 'JT-023 kW/tph steady AND SA01 P80 rising',
    status: 'proposed',
    outcome: 'both',
  },
  {
    id: 'fm-grate-wear',
    unitOpType: 'ball_mill',
    circuit: 'Grinding',
    failureMode: 'Mill discharge grates worn or blocked',
    cause: 'Grate slot wear lets oversize through, or a blocked panel starves flow -- either way the cyclone feed changes',
    effect: 'PSA coarsens with cyclone overflow density falling (fines bypass) -- not a media or ore-hardness signature',
    s: 7,
    o: 4,
    d: 6,
    rpn: 168, // [syn] -- estimated alongside fm-apex (cyclone-side wear, same RPN band)
    earlyWarning: 'SA01 P80 rising AND DT-204 cyclone O/F density falling',
    status: 'proposed',
    outcome: 'both',
  },
  {
    id: 'fm-belt-weigher',
    unitOpType: 'ball_mill',
    circuit: 'Grinding',
    failureMode: 'Belt weigher (WIT-021) reading inaccurate, mill overloaded',
    cause: 'Weigher calibration drift under-reports true tonnage; the feed loop holds WIT-021 on setpoint while the mill actually overloads',
    effect: 'PSA coarsens with WIT-021 reading steady and kW/tph rising -- the overload is invisible on the feed display alone',
    s: 7,
    o: 3,
    d: 7,
    rpn: 147, // [syn] -- instrument-fault RPN band, lower O than mechanical wear (calibration drift is rarer) and higher D (masked by the loop itself)
    earlyWarning: 'WIT-021 steady AND JT-023 kW/tph rising AND SA01 P80 rising',
    status: 'proposed',
    outcome: 'both',
  },
  {
    id: 'fm-air-flow',
    unitOpType: 'flotation',
    circuit: 'Pb / Zn rougher',
    failureMode: 'Rougher air flow off design (too high or too low)',
    cause: 'Blower trim or valve drift moves cell air away from design -- distinct from dr-air-valve (valve closed / blocked)',
    effect: 'Recovery falls -- too little air under-floats, too much air pulls gangue and collapses the froth',
    s: 6,
    o: 5,
    d: 5,
    rpn: 150, // [syn] -- flotation operating-parameter RPN band, alongside fm-zn-ph
    earlyWarning: 'FI-403 / FI-503 air flow excursion AND circuit recovery falling',
    status: 'proposed',
    outcome: 'recovery',
  },
  {
    id: 'fm-froth-height',
    unitOpType: 'flotation',
    circuit: 'Pb / Zn rougher',
    failureMode: 'Froth height too low',
    cause: 'Level setpoint or air rate pulls the froth bed down below the design band',
    effect: 'Concentrate grade rises as recovery falls -- a thin froth over-cleans at the expense of mass pull',
    s: 6,
    o: 4,
    d: 5,
    rpn: 120, // [syn] -- flotation operating-parameter RPN band, alongside fm-zn-ph
    earlyWarning: 'FR-PB01 / FR-ZN01 froth depth below band AND concentrate grade rising AND recovery falling',
    status: 'proposed',
    outcome: 'both',
  },
];

// ---------------------------------------------------------------------------
// Module 3 rule rows — the four behind the deck findings plus the doc's §6
// worked examples (tim-scope advisory-loop §7.2). Same DiagnosticRule shape
// as the Module 1 rows; kept as a separate export until the rule table merges.
// ---------------------------------------------------------------------------

export const ADVISORY_RULES: readonly DiagnosticRule[] = [
  {
    id: 'dr-zn-ph',
    signalPattern: 'circuit.zn_ph in-spec < 95% of shift + reagent.smbs_actual > design', // B: AI Performance finding 1; tim-scope DR-ZN-PH-01
    diagnosis: 'pH instability suppressing CuSO₄ activation; SMBS carry-over co-cause',
    priority: 'P2',
    responsible: 'metallurgist',
    causeCategory: 'metallurgical-process', // B6: prose-only rule (no structured `when`) — never matched; category for the review route
    recommendedAction: 'Increase lime SP to Zn circuit — target pH 11.0–11.2; check Pb-circuit SMBS dosing before concluding the lime loop alone is at fault.',
    symptomGroup: 'Zn Recovery Below Target',
    affects: ['zn-recovery'],
    sourceCauseId: ['MD-021', 'MD-025'], // paul-fmea.A3: pH too low (MD-021) and too high (MD-025) — this rule's "pH instability" diagnosis covers both directions of the same loop
  },
  // review SHOULD S7 — `dr-ag-3418a`, `dr-air-valve` and `dr-cuso4` (below)
  // each carry `symptomGroup` / `affects` / `sourceCauseId`, wiring them into
  // the #270 workbook cross-reference the same way a live rule is — but none
  // of the three has a backing `Claim` anywhere in `bunker-hill-rule-claims.ts`
  // (no `CASE_OUTCOMES` row names them as `proposedRuleId`, so they get no
  // case claim; they aren't in `DIAGNOSTIC_RULES`, so they get no workbook
  // claim either). That makes their lifecycle state neither `covered` nor
  // `proposed` — it's inert/orphaned: metadata with no claim to carry it
  // through review. The Rule Set panel and retrieval thread correctly render
  // their named MD-### causes as `gap` regardless of this metadata, because
  // `liveRuleSet(advisory)` only ever walks `rules`/`proposed`, both of which
  // are claim-derived. This is not a bug to fix in this pass — orphaned rows
  // are exactly the honest state Advisory should show while these three sit
  // unreviewed — but it is worth a claim (workbook or case) whenever a
  // reviewer actually looks at them, or the metadata should come out.
  {
    id: 'dr-ag-3418a',
    signalPattern: 'reagent.3418a_dose < design + Ag in Pb/Ag conc falling for consecutive shifts', // B: AI Performance finding 2; K20 round 1 (#717) — the mode is a falling grade, not a level below design
    diagnosis: 'Collector underdose (3418A)',
    priority: 'P2',
    responsible: 'metallurgist',
    causeCategory: 'metallurgical-process',
    recommendedAction: 'Restore 3418A to 45 g/t; request Ag assay on Zn concentrate; calibrate Pb/Ag thickener density instrument.',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'reagent-3418a-rate', condition: 'below-band', label: '3418A dose below design' },
      { signal: 'ag-recovery', condition: 'falling', label: 'Ag recovery falling' },
    ],
    rival: 'dr-pb-ph-high',
    discriminator: {
      signal: 'ag-con-grade',
      compare: '<',
      threshold: 10,
      evidenceKind: 'lab-sample',
      label: 'Ag in Pb/Ag concentrate assay — confirms collector underdose',
    },
    symptomGroup: 'Pb Recovery Below Target',
    affects: ['pb-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-004'],
  },
  {
    id: 'dr-p80-apex',
    signalPattern: 'cyclone O/F P80 rising over six weeks + cyclone feed pump VSD rising at constant discharge pressure',
    diagnosis: 'Grind coarsening from apex liner wear / cyclone pack degradation — not media depletion',
    priority: 'P3',
    responsible: 'maintenance',
    causeCategory: 'mechanical-electrical',
    recommendedAction: 'Inspect apex and vortex finder; schedule cyclone inspection work request.',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-p80', condition: 'rising-long', label: 'SA01 P80 rising over six weeks' },
      { signal: 'cyclone-pump-vsd', condition: 'rising-long', label: 'Cyclone feed pump VSD rising at constant discharge pressure' },
      { signal: 'mill-power-ratio', condition: 'stable', label: 'JT-023 kW/tph steady — not media depletion' },
    ],
    rival: 'dr-media',
    discriminator: {
      signal: 'grind-p80',
      compare: '>',
      threshold: 78,
      evidenceKind: 'visual',
      label: 'Apex and vortex-finder inspection — wear confirms cyclone pack degradation',
    },
    ...causeRow('MD-040'),
    sourceCauseId: ['MD-040'],
  },
  {
    id: 'dr-as-ore',
    signalPattern: 'As in Pb/Ag conc rising + tails As stable + no reagent step-change', // B: AI Performance finding 4
    diagnosis: 'Ore-source arsenic (arsenopyrite) as mining advances — not process',
    priority: 'P4',
    responsible: 'superintendent',
    causeCategory: 'feed-ore', // B6: prose-only rule (no structured `when`) — never matched; category for the review route
    recommendedAction: 'Request As assays on ROM blast samples; consider depressing arsenopyrite (Pb rougher pH 7.8–8.0); alert smelter trading.',
    symptomGroup: 'Concentrate Penalty Elements / Impurities',
    affects: ['penalty-elements'],
    sourceCauseId: ['MD-054'],
  },
  // Orphaned lifecycle metadata — see the S7 comment above `dr-ag-3418a`.
  {
    id: 'dr-air-valve',
    signalPattern: 'air FIT zero + blower DI = running', // A §M3 6.1 — the zero-touch target state
    diagnosis: 'Air valve closed or blocked',
    priority: 'P2',
    responsible: 'operator',
    causeCategory: 'mechanical-electrical',
    recommendedAction: 'Open / clear the air valve — Immediate Response Action routed to Operations, no investigation step.',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-air', condition: 'below-band', label: 'Pb rougher air FIT at zero' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    rival: 'dr-air-flow-pb',
    discriminator: {
      signal: 'pb-rougher-froth',
      compare: '<',
      threshold: 120,
      evidenceKind: 'visual',
      label: 'Visual check — valve closed or blocked with blower running',
    },
    symptomGroup: 'Pb Recovery Below Target',
    affects: ['pb-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-006'],
  },
  // Orphaned lifecycle metadata — see the S7 comment above `dr-ag-3418a`.
  {
    id: 'dr-cuso4',
    signalPattern: 'CuSO₄ FIT on setpoint + Zn tails rising', // A §M3 6.2
    diagnosis: 'CuSO₄ solution degraded (vs pump/valve fault if the FIT itself is low)',
    priority: 'P3',
    responsible: 'operator',
    causeCategory: 'metallurgical-process',
    recommendedAction: 'Titrate CuSO₄ solution strength (CRO arranges, Metallurgy interprets) before touching dosing.',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'reagent-cuso4-rate', condition: 'stable', label: 'CuSO₄ FIT on setpoint' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-zn-ph',
    discriminator: {
      signal: 'zn-conditioner-ph',
      compare: '<',
      threshold: 10.8,
      evidenceKind: 'lab-sample',
      label: 'CuSO₄ titration — degraded solution confirms activation failure',
    },
    symptomGroup: 'Zn Recovery Below Target',
    affects: ['zn-recovery'],
    sourceCauseId: ['MD-020'],
  },
  {
    id: 'dr-tramp',
    signalPattern: 'crusher kW spike + rising bowl-relief count', // A §M3 6.3 — Crushing FMEA, Hydrocone, RPN 160
    diagnosis: 'Tramp iron',
    priority: 'P3',
    responsible: 'maintenance',
    causeCategory: 'mechanical-electrical', // B6: prose-only rule (no structured `when`) — never matched; category for the review route
    recommendedAction: 'Visually inspect the CV-001 magnet; review maintenance log for tramp type and source. Lab: none — physical event.',
  },
  {
    id: 'dr-dart-valve',
    signalPattern: 'LT-140/141 high + all reagents on setpoint + air on setpoint + loop LIC4010 in AUTO', // A §M3 6.4 — controlMode clause added via FMR-0006
    diagnosis: 'Cell level operational cause — instrument or hydraulic fault (in AUTO); manual-held level is not a case',
    priority: 'P3',
    responsible: 'maintenance',
    causeCategory: 'mechanical-electrical', // B6: prose-only rule (no structured `when`) — never matched; category for the review route
    recommendedAction: 'Check dart-valve actuator and level instrument only if the loop is in AUTO; if in Manual, ask the operator why before raising work.',
    symptomGroup: 'Pb Recovery Below Target',
    affects: ['pb-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-007'], // paul-fmea.A3: "Cell level too high" — not MD-010 (oxidised minerals is a different mechanism)
  },
  {
    id: 'dr-mineralogy',
    signalPattern: 'Recovery drops in both circuits with all process variables correct + mine has advanced',
    diagnosis: 'Ore mineralogy step-change, not a grind problem — distinguished by PSA staying on-target',
    priority: 'P3',
    responsible: 'metallurgist',
    recommendedAction: 'Check mine advance and ore source before touching the process — PSA on-target rules out grind.',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-conditioner-ph', condition: 'excursion', label: 'Zn float pH excursion' },
    ],
    rival: 'dr-grind-fail',
    symptomGroup: 'Both Pb + Zn Recovery Declining',
    affects: ['pb-recovery', 'zn-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-033'],
  },
  // --- rules2 (paul-email.A5 / paul-call.A5-A6, #250) -----------------------
  // Seven structured rows covering the five causes on Paul's grinding +
  // flotation lists that had no rule. Two causes (air flow, froth height)
  // split one row per circuit -- a DiagnosticRule.subprocessId is singular
  // and an excursion must sit inside it (docs/engines/diagnosis.md 'The
  // algorithm' step 2), so one row cannot span sp-pb and sp-zn; the same
  // constraint the brief already applied to air flow is applied here to
  // froth height too (rules2-build-report.md 'Ambiguity decisions').
  // Every row enters through a pending FMEAUpdateReview (FMR-0010..FMR-0016)
  // as a disputed case claim -- proposed, not live, until approved
  // (docs/engines/rule-set.md). Primary clause (when[0]) is the clause that
  // reads false at the default cursor, matching the dr-media / dr-hardness
  // convention, so the quiet-at-t0 assertion holds structurally and not by
  // accident of the excursion gate alone.
  {
    id: 'dr-media-size',
    signalPattern: 'JT-023 kW/tph steady + PSA coarsening (not media wear, not hardness)',
    diagnosis: 'Media size distribution wrong for the ore — grinding efficiency lost independent of media wear or ore hardness',
    priority: 'P2',
    responsible: 'maintenance',
    recommendedAction: 'Pull a ball-charge size sample at the next mill inspection; recharge to the design size distribution.',
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-p80', condition: 'rising', label: 'SA01 P80 rising' },
      { signal: 'mill-power-ratio', condition: 'stable', label: 'JT-023 kW/tph steady' },
    ],
    rival: 'dr-media',
    discriminator: {
      signal: 'grind-p80',
      compare: '>',
      threshold: 88,
      evidenceKind: 'visual',
      label: 'Ball-charge sizing check at the next mill inspection — undersize media confirmed',
    },
    symptomGroup: 'Additional Grinding Circuit Root Causes',
    affects: ['pb-recovery', 'zn-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-040'],
  },
  {
    id: 'dr-grate-wear',
    signalPattern: 'PSA coarsening + cyclone overflow density falling — grates worn or blocked, not apex/pressure',
    diagnosis: 'Mill discharge grates worn or blocked — oversize passing (or flow starved) ahead of the cyclone',
    priority: 'P2',
    responsible: 'maintenance',
    recommendedAction: 'Inspect discharge grates for wear or blockage at the next mill stop; clear or replace before the next campaign.',
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-p80', condition: 'rising', label: 'SA01 P80 rising' },
      { signal: 'cyclone-overflow-density', condition: 'falling', label: 'DT-204 cyclone O/F density falling' },
    ],
    // R2-F7: dr-p80-apex is prose-only (no `when`), so scoreRule's rival
    // term is permanently 0 against it -- dr-grate-wear would collect full
    // rival credit for beating a rule that never runs. dr-media-size reads
    // the same P80 rise and has a `when` clause, so it actually scores.
    rival: 'dr-media-size',
    discriminator: {
      signal: 'grind-p80',
      compare: '>',
      threshold: 90,
      evidenceKind: 'visual',
      label: 'Grate inspection — media or oversize in the discharge confirms wear or blockage',
    },
  },
  {
    id: 'dr-belt-weigher',
    signalPattern: 'WIT-021 reading steady + JT-023 kW/tph rising + PSA coarsening — the mill is overloaded but the feed display does not show it',
    diagnosis: 'Belt weigher (WIT-021) inaccurate, overloading the mill — the feed loop holds a bad reading on setpoint',
    priority: 'P2',
    responsible: 'maintenance',
    recommendedAction: 'Recalibrate WIT-021 against a hand-check; verify true feed tonnage before assuming media or hardness.',
    // causeCategory: mechanical-electrical, not feed-ore — this is an instrument
    // calibration fault (Greg's mech/E/I split), not a change in the ore itself;
    // recorded per Q1's "record why".
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'mill-power-ratio', condition: 'rising', label: 'JT-023 kW/tph rising' },
      { signal: 'mill-feed-rate', condition: 'stable', label: 'WIT-021 reading steady despite the overload' },
      { signal: 'grind-p80', condition: 'rising', label: 'SA01 P80 rising' },
    ],
    rival: 'dr-hardness',
    // R2-F5: the discriminator must not re-read WIT-021 -- that is the
    // instrument this rule accuses of lying, and DiagnosisEngine's
    // evaluateDiscriminator falls back to a signal's own latest reading
    // when no evidence has been answered, so a WIT-021 discriminator would
    // dock confidence for the rule being right. grind-p80 is independent
    // of the `mill-feed-rate stable` clause this rule requires and is the
    // hand-check that actually confirms an overloaded mill.
    discriminator: {
      signal: 'grind-p80',
      compare: '>',
      threshold: 85,
      evidenceKind: 'lab-sample',
      label: 'Hand-check tonnage against the SA01 P80 the mill is actually producing',
    },
  },
  {
    id: 'dr-air-flow-pb',
    signalPattern: 'FI-403 Pb rougher air flow excursion (high or low) + Pb recovery falling',
    diagnosis: 'Pb rougher air flow off design — too little air under-floats, too much pulls gangue and collapses the froth',
    priority: 'P2',
    responsible: 'operator',
    recommendedAction: 'Trim FI-403 back to the design band; confirm against froth appearance before touching reagents.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-air', condition: 'excursion', label: 'FI-403 air flow excursion' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    discriminator: {
      signal: 'pb-rougher-froth',
      compare: '<',
      threshold: 150,
      evidenceKind: 'visual',
      label: 'Froth appearance check — thin, fast-breaking froth confirms air rate off design',
    },
    symptomGroup: 'Pb Recovery Below Target',
    affects: ['pb-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-006'],
  },
  {
    id: 'dr-air-flow-zn',
    signalPattern: 'FI-503 Zn rougher air flow excursion (high or low) + Zn recovery falling',
    diagnosis: 'Zn rougher air flow off design — too little air under-floats, too much pulls gangue and collapses the froth',
    priority: 'P2',
    responsible: 'operator',
    recommendedAction: 'Trim FI-503 back to the design band; confirm against froth appearance before touching reagents.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-rougher-air', condition: 'excursion', label: 'FI-503 air flow excursion' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    discriminator: {
      signal: 'zn-rougher-froth',
      compare: '<',
      threshold: 130,
      evidenceKind: 'visual',
      label: 'Froth appearance check — thin, fast-breaking froth confirms air rate off design',
    },
    symptomGroup: 'Zn Recovery Below Target',
    affects: ['zn-recovery'],
    sourceCauseId: ['MD-023'],
  },
  {
    id: 'dr-froth-height-pb',
    signalPattern: 'FR-PB01 froth depth below band + Pb concentrate grade rising with Pb recovery falling',
    diagnosis: 'Pb rougher froth height too low — a thin froth over-cleans at the expense of mass pull',
    priority: 'P3',
    responsible: 'operator',
    recommendedAction: 'Raise the Pb rougher level setpoint to restore froth depth; recheck grade/recovery next shift.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-froth', condition: 'below-band', label: 'FR-PB01 froth depth below band' },
      { signal: 'pb-con-grade', condition: 'rising', label: 'Pb concentrate grade rising' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    // R2-F6: the discriminator must not restate the trigger's own froth
    // signal (any value that satisfies "below-band" would also satisfy a
    // second froth-depth check, so it separates nothing). Air rate is the
    // independent evidence that separates a level-driven froth collapse
    // from an air-driven one -- FI-403 on design rules air out, mirroring
    // dr-air-flow-pb's own use of froth depth as evidence for its air-rate
    // trigger, in reverse.
    discriminator: {
      signal: 'pb-rougher-air',
      compare: '<=',
      threshold: 13,
      evidenceKind: 'visual',
      label: 'Air rate check — FI-403 not running high, ruling out an air-driven froth collapse',
    },
  },
  {
    id: 'dr-froth-height-zn',
    signalPattern: 'FR-ZN01 froth depth below band + Zn concentrate grade rising with Zn recovery falling',
    diagnosis: 'Zn rougher froth height too low — a thin froth over-cleans at the expense of mass pull',
    priority: 'P3',
    responsible: 'operator',
    recommendedAction: 'Raise the Zn rougher level setpoint to restore froth depth; recheck grade/recovery next shift.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-rougher-froth', condition: 'below-band', label: 'FR-ZN01 froth depth below band' },
      { signal: 'zn-con-grade', condition: 'rising', label: 'Zn concentrate grade rising' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    // R2-F6: same fix as dr-froth-height-pb -- independent air-rate
    // evidence in place of a second froth-depth read.
    discriminator: {
      signal: 'zn-rougher-air',
      compare: '<=',
      threshold: 15,
      evidenceKind: 'visual',
      label: 'Air rate check — FI-503 not running high, ruling out an air-driven froth collapse',
    },
  },
  // --- paper-sourced (paul-fmea.A7, #274) -----------------------------------
  // One row from a real, citable literature source (not a plant artefact) —
  // authority rank below `fmea` (RULE_AUTHORITY). Proposed via FMR-0017;
  // never live at seed — a paper is not plant ground truth (docs/schema/
  // source-document.md). Prose-only: no seeded signal carries a penalty-
  // element assay, so `when` would be unmatchable and dishonest.
  {
    id: 'dr-sb-bi-depression',
    signalPattern: 'Pb/Ag concentrate Sb or Bi penalty assay elevated + no dedicated depressant stage in the reagent scheme',
    diagnosis: 'Sb/Bi sulfosalts (tetrahedrite-tennantite, bismuthinite) co-floating with galena, undepressed — literature-proposed, not yet a workbook rule',
    priority: 'P3',
    responsible: 'metallurgist',
    recommendedAction: 'Trial pH/pulp-potential depression or starvation collector dosing per Smith, Bruckard & Sparrow (2022) before adding reagent spend; confirm against a penalty-element assay panel (MD-060 remains its own open gap).',
    causeCategory: 'metallurgical-process',
    symptomGroup: 'Concentrate Penalty Elements / Impurities',
    affects: ['penalty-elements'],
    sourceCauseId: ['MD-054'],
  },
];

// ---------------------------------------------------------------------------
// KPI definitions, targets and values — the GM startup table as data
// (B: GM "Startup performance · design vs ramp target vs actual (month 4)").
// Deltas are carried, not recomputed (seed-data rule R4; S13: table wins over cards).
// ---------------------------------------------------------------------------

export const KPI_DEFINITIONS: readonly KpiDefinition[] = [
  { id: 'kpi-ore-tpd', name: 'Ore processed', unit: 't/d', scope: 'plant', source: 'DCS' },
  { id: 'kpi-zn-rec', name: 'Zn recovery', unit: '%', scope: 'plant', source: 'derived' },
  { id: 'kpi-pb-rec', name: 'Pb recovery', unit: '%', scope: 'plant', source: 'derived' },
  { id: 'kpi-ag-rec', name: 'Ag recovery', unit: '%', scope: 'plant', source: 'derived' },
  { id: 'kpi-mill-util', name: 'Mill utilisation', unit: '%', scope: 'plant', source: 'DCS' },
  { id: 'kpi-zn-grade', name: 'Zn conc grade', unit: '% Zn', scope: 'plant', source: 'LIMS' },
  { id: 'kpi-ag-grade', name: 'Ag in Pb/Ag conc', unit: 'oz/t', scope: 'plant', source: 'LIMS' },
];

const GM = 'B: GM startup table'; // S1: superseded — PFS §7.1 design row is now the seed basis (seed-retie #258); deck values on provenance below
const PFS = 'A PFS §7.1 design row (deck design says the pre-retie GM startup-table figure)'; // seed-retie #258

export const TARGETS: readonly Target[] = [
  { kpiId: 'kpi-ore-tpd', basis: 'design', period: '24h', value: 1800, provenance: GM },
  { kpiId: 'kpi-ore-tpd', basis: 'budget', period: '24h', value: 1440, provenance: 'FD Month-4 ramp: 60 stph → 1,440 stpd, 80% of 1,800 design (deck design says 1,800) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]' },
  { kpiId: 'kpi-zn-rec', basis: 'design', period: '24h', value: 85.1, provenance: PFS },
  { kpiId: 'kpi-zn-rec', basis: 'budget', period: '24h', value: 69.8, provenance: 'FD Month-4 ramp = 82% of PFS design 85.1% (deck design says 88.0%) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]' },
  { kpiId: 'kpi-pb-rec', basis: 'design', period: '24h', value: 88.2, provenance: PFS },
  { kpiId: 'kpi-pb-rec', basis: 'budget', period: '24h', value: 75.0, provenance: 'FD Month-4 ramp = 85% of PFS design 88.2% (deck design says 85.0%) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]' },
  { kpiId: 'kpi-ag-rec', basis: 'design', period: '24h', value: 84.2, provenance: PFS },
  { kpiId: 'kpi-ag-rec', basis: 'budget', period: '24h', value: 71.6, provenance: 'FD Month-4 ramp = 85% of PFS design 84.2% (deck design says 93.0%) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]' },
  // No 'budget' row for kpi-zn-grade / kpi-ag-grade: the FD gives no ramp or
  // budget grade for concentrate (Paul states none). A budget row at the
  // design value would print "vs Budget 58.0 · design 58.0" — one number
  // under two names — and assert a document that does not exist. Both
  // resolve to 'design', the one basis these figures actually are
  // (pl.D1 round 3, #142, 2026-09-09; planning-flip.md question 3).
  { kpiId: 'kpi-zn-grade', basis: 'design', period: '24h', value: 58.0, provenance: PFS },
  { kpiId: 'kpi-ag-grade', basis: 'design', period: '24h', value: 12.13, provenance: PFS },
];

export const KPI_VALUES: readonly KpiValue[] = [
  { kpiId: 'kpi-ore-tpd', period: '24h', scopeRef: 'plant', value: 1720, deltaVsTarget: '+19.4%', deltaVsDesign: '−4.4%', provenance: GM },
  { kpiId: 'kpi-zn-rec', period: '24h', scopeRef: 'plant', value: 82.3, deltaVsTarget: '+17.9%', deltaVsDesign: '−3.3%', provenance: PFS },
  {
    kpiId: 'kpi-pb-rec',
    period: '24h',
    scopeRef: 'plant',
    value: Number.parseFloat(PB_RECOVERY_TABLE.value),
    deltaVsTarget: PB_RECOVERY_TABLE.deltaVsTarget,
    deltaVsDesign: PB_RECOVERY_TABLE.deltaVsDesign,
    provenance: PFS,
  },
  { kpiId: 'kpi-ag-rec', period: '24h', scopeRef: 'plant', value: 82.7, deltaVsTarget: '+15.5%', deltaVsDesign: '−1.8%', provenance: PFS },
  { kpiId: 'kpi-zn-grade', period: '24h', scopeRef: 'plant', value: 58.4, deltaVsTarget: '+0.7%', deltaVsDesign: '+0.7%', provenance: PFS },
  { kpiId: 'kpi-ag-grade', period: '24h', scopeRef: 'plant', value: 12.14, deltaVsTarget: '+0.1%', deltaVsDesign: '+0.1%', provenance: PFS },
];
